import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAccount } from "wagmi";
import { loadCanvas, saveCanvas, CanvasApiError } from "../../lib/canvas-api";
import { FreezeModal, type FreezeParcel } from "../artwork/FreezeModal";
import { usePublication } from "../dashboard/usePublication";
import { listAssets, mediaContentUrl, type MediaAsset } from "../../lib/media-api";
import { useIsDesktop } from "../media";
import { useMediaLibrary } from "../dashboard/useMediaLibrary";
import { AutoFitText } from "../../viewer/AutoFitText";
import { SocialRender } from "../../viewer/SocialRender";
import { ContactsRender } from "../../viewer/ContactsRender";
import { PdfCard } from "../../viewer/PdfCard";
import { FitToBox } from "../../viewer/FitToBox";
import { LinkRender } from "../../viewer/LinkRender";
import { PixelArtRender } from "../../viewer/PixelArtRender";
import { PixelStudio } from "./PixelStudio";
import { TemplatePicker } from "./TemplatePicker";
import { buildTemplate, type TemplateDef } from "./templates";
import { useOwnedFrozenArtworks, type PlacedFrozenArtwork } from "./useOwnedFrozenArtworks";
import { CanvasFrozenArtworkLayer } from "./CanvasFrozenArtworkLayer";
import { Canvas3DFootprintLayer } from "./Canvas3DFootprintLayer";
import { loadDraft3D, saveDraft3D, Territory3DApiError, type Draft3DAuthority } from "../../lib/territory3d-api";
import { ownedCells, validateScene, translateScene, rotateScene90, type WorkArt } from "../../domain/voxel3d";
import { PlacementControls } from "../artwork/PlacementControls";
import { SOCIAL_PLATFORM_OPTIONS, SOCIAL_PLATFORM_LABELS } from "../../viewer/social-icons";
import { CONTACT_KIND_OPTIONS } from "../../viewer/contact-uris";
import {
  History, TIER_COLORS, COLOR_HEX, FONT_STACKS, hexOf, makeElement,
  bringToFront, sendToBack, bringForward, sendBackward, normalizeZ, elementBBox,
  intersectsTerritory, snapValue, MAX_ELEMENTS, authorizedParcels, hasVisibleFootprint,
  socialStyleWithDefaults, SOCIAL_PRESET_DEFAULTS,
  contactsStyleWithDefaults, CONTACTS_PRESET_DEFAULTS,
  linkStyleWithDefaults, LINK_PRESET_DEFAULTS, PIXEL_ART_SIZE_PRESETS, PIXEL_ART_MAX_DIM,
  type Box,
  type CanvasDoc, type CanvasElement, type CanvasStyle, type ModuleType, type ShapeSubtype, type Tier,
  type SocialStyle, type SocialPreset, type ContactsStyle, type ContactsPreset,
  type LinkStyle, type LinkPreset,
} from "../../domain/canvas-model";
import { gridPowerCapacity } from "../../domain/grid-power";
import { visibleRatioBBox, elementCostHalf, gpMeter, moduleBaseHalf, halfToGp, ellipseAuthorizedRatio, boxAabb } from "../../domain/gp-cost";
import { API_BASE } from "../../lib/api";
import { OwnerModerationBanner } from "../moderation/OwnerModerationBanner";
import { OnboardingContextGuide } from "../onboarding/OnboardingContextGuide";
import { cn } from "../cn";
import { useT } from "../../i18n";

function assetContentUrl(assetId: string): string {
  return `${API_BASE}/api/v1/media/assets/${encodeURIComponent(assetId)}/content`;
}

type SaveState = "saved" | "dirty" | "saving" | "error";
interface View { scale: number; tx: number; ty: number }

const GLOW_PX: Record<string, number> = { OFF: 0, LOW: 6, MEDIUM: 14, HIGH: 26 };

// In-editor Media Library panel — accepted MIME + a compact byte formatter so a
// creator can upload/browse media WITHOUT leaving the canvas (round-trip fix).
const CANVAS_MEDIA_ACCEPT = "image/png,image/jpeg,image/webp,image/gif,application/pdf,video/mp4,video/webm";
function fmtSize(n: number): string {
  const MiB = 1048576, GiB = 1073741824;
  if (n >= GiB) return `${(n / GiB).toFixed(2)} GiB`;
  if (n >= MiB) return `${(n / MiB).toFixed(1)} MiB`;
  if (n >= 1024) return `${Math.round(n / 1024)} KiB`;
  return `${n} B`;
}

export function CanvasEditorRoute({ identityId }: { identityId: string | null }) {
  const t = useT();
  const { address, isConnected } = useAccount();
  const [doc, setDoc] = useState<CanvasDoc | null>(null);
  const [error, setError] = useState<{ code: string; message: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const genRef = useRef(0);
  const walletRef = useRef<string | undefined>(address);
  walletRef.current = address;

  const reload = useCallback(() => {
    if (!identityId || !isConnected || !address) { setLoading(false); return; }
    const myGen = ++genRef.current;
    const myWallet = address;
    const controller = new AbortController();
    setLoading(true); setError(null);
    loadCanvas(identityId, controller.signal)
      .then((c) => {
        // Stale wallet / stale generation suppression (Work Order §79).
        if (myGen !== genRef.current || walletRef.current !== myWallet) return;
        setDoc(c); setLoading(false);
      })
      .catch((e: unknown) => {
        if (controller.signal.aborted || myGen !== genRef.current || walletRef.current !== myWallet) return;
        const err = e as CanvasApiError;
        setError({ code: err.code ?? "CANVAS_ERROR", message: err.message ?? t("editor.canvas.failedToLoad") });
        setLoading(false);
      });
    return () => controller.abort();
  }, [identityId, isConnected, address]);

  useEffect(() => { const c = reload(); return c; }, [reload]);

  if (!identityId) return <Shell><Msg testid="canvas-invalid">{t("editor.canvas.invalid")}</Msg></Shell>;
  if (!isConnected || !address) return <Shell><Msg testid="canvas-auth-required">{t("editor.canvas.authRequired")} <a className="text-neon-teal" href="#auth">{t("editor.canvas.signIn")}</a></Msg></Shell>;
  if (loading) return <Shell><Msg testid="canvas-loading">{t("editor.canvas.loading")}</Msg></Shell>;
  if (error) return <Shell><Msg testid="canvas-error">{error.code}: {error.message} <a className="text-neon-teal ml-2" href="#dashboard">{t("editor.canvas.back")}</a></Msg></Shell>;
  if (!doc) return <Shell><Msg testid="canvas-empty-state">{t("editor.canvas.empty")}</Msg></Shell>;
  return <CanvasEditor key={doc.identity_id} initial={doc} onReload={reload} />;
}

function Shell({ children }: { children: React.ReactNode }) {
  return <div data-testid="canvas-editor-route" className="w-full h-full bg-obsidian text-text-primary">{children}</div>;
}
function Msg({ children, testid }: { children: React.ReactNode; testid: string }) {
  return <div data-testid={testid} className="mx-auto mt-24 w-[min(560px,90%)] rounded-lg border border-border-soft bg-panel p-8 text-[13px]">{children}</div>;
}

function CanvasEditor({ initial, onReload }: { initial: CanvasDoc; onReload: () => void }) {
  const t = useT();
  const isDesktop = useIsDesktop();
  const [elements, setElements] = useState<CanvasElement[]>(() => normalizeZ(initial.elements));
  const [style, setStyle] = useState<CanvasStyle>(initial.style);
  const [selected, setSelected] = useState<string[]>([]);
  const [saveState, setSaveState] = useState<SaveState>("saved");
  const [saveErr, setSaveErr] = useState<{ code: string; message: string } | null>(null);
  const [errorOpen, setErrorOpen] = useState(false);
  const [view, setView] = useState<View>({ scale: 1, tx: 0, ty: 0 });
  const [mediaPicker, setMediaPicker] = useState<null | ModuleType>(null);
  const [pdfBgTarget, setPdfBgTarget] = useState<string | null>(null);
  const [pixelSizePicker, setPixelSizePicker] = useState(false);
  const [pixelDrawMode, setPixelDrawMode] = useState(false);
  const [pixelStudioId, setPixelStudioId] = useState<string | null>(null);
  const [libOpen, setLibOpen] = useState(false);
  const [templateOpen, setTemplateOpen] = useState(false);
  const [freezeSel, setFreezeSel] = useState<CanvasElement | null>(null);
  const [placementArtworkId, setPlacementArtworkId] = useState<string | null>(null);
  const pub = usePublication(initial.identity_id);
  const [savedRevision, setSavedRevision] = useState<number>(initial.revision);
  const historyRef = useRef(new History());
  const stageRef = useRef<HTMLDivElement | null>(null);
  const territory = initial.territory;
  const frozenArtworks = useOwnedFrozenArtworks(territory, true);
  const docForClip: CanvasDoc = useMemo(() => ({ ...initial, elements, style }), [initial, elements, style]);

  // ── Macro #26 · contest polish — the owner's published 3D scene is shown on
  // the 2D Canvas as its top-down projection, and DRAGGING it moves the REAL 3D
  // placement (X,Y) inside the owned footprint. Writes go through the certified
  // 3D Draft authority so the 2D Canvas and the 3D scene stay coherent; a move
  // that would leave the owned parcels is rejected (blocked) via validateScene.
  const [workArts3d, setWorkArts3d] = useState<WorkArt[]>([]);
  const auth3dRef = useRef<Draft3DAuthority | null>(null);
  const drag3dOrig = useRef<WorkArt[] | null>(null);
  const moved3d = useRef(false);
  const [notice3d, setNotice3d] = useState<string | null>(null);
  const flash3d = useCallback((m: string) => {
    setNotice3d(m);
    window.setTimeout(() => setNotice3d((n) => (n === m ? null : n)), 2400);
  }, []);
  const cells3d = useMemo(
    () => ownedCells((territory?.parcels ?? []).map((p) => ({ x: p.x, y: p.y, w: p.w, h: p.h }))),
    [territory],
  );
  useEffect(() => {
    const c = new AbortController();
    loadDraft3D(initial.identity_id, c.signal)
      .then((d) => { auth3dRef.current = d.authority; setWorkArts3d(d.work_arts ?? []); })
      .catch(() => { /* fail-soft: no 3D overlay on this territory */ });
    return () => c.abort();
  }, [initial.identity_id]);

  const save3d = useCallback(async (arts: WorkArt[]) => {
    if (!auth3dRef.current) return;
    try {
      const saved = await saveDraft3D(initial.identity_id, arts, auth3dRef.current);
      auth3dRef.current = saved.authority;
      setWorkArts3d(saved.work_arts);
      flash3d(t("editor.canvas.flash.saved3d"));
    } catch (e) {
      const err = e as Territory3DApiError;
      flash3d(err.code === "TERRITORY_CHANGED" ? t("editor.canvas.flash.territoryChanged3d") : t("editor.canvas.flash.error3d", { code: err.code }));
    }
  }, [initial.identity_id, flash3d, t]);

  const onScene3dDragStart = useCallback(() => { drag3dOrig.current = workArts3d; moved3d.current = false; }, [workArts3d]);
  const onScene3dDrag = useCallback((dx: number, dy: number) => {
    const orig = drag3dOrig.current;
    if (!orig) return;
    if (dx === 0 && dy === 0) { setWorkArts3d(orig); return; }
    const moved = translateScene(orig, dx, dy);
    if (validateScene(moved, cells3d).ok) { setWorkArts3d(moved); moved3d.current = true; }
  }, [cells3d]);
  const onScene3dDragEnd = useCallback(() => {
    drag3dOrig.current = null;
    if (!moved3d.current) return;
    moved3d.current = false;
    setWorkArts3d((cur) => { void save3d(cur); return cur; });
  }, [save3d]);
  const rotate3d = useCallback(() => {
    setWorkArts3d((cur) => {
      const rotated = rotateScene90(cur);
      if (rotated && validateScene(rotated, cells3d).ok) { void save3d(rotated); return rotated; }
      flash3d(t("editor.canvas.flash.rotateOutside"));
      return cur;
    });
  }, [cells3d, save3d, flash3d, t]);

  useEffect(() => { historyRef.current.push({ elements: normalizeZ(initial.elements), style: initial.style }); }, [initial]);

  const markDirty = useCallback((next: CanvasElement[], nextStyle?: CanvasStyle) => {
    historyRef.current.push({ elements: next, style: nextStyle ?? style });
    setSaveState("dirty");
  }, [style]);

  const commit = useCallback((next: CanvasElement[], nextStyle?: CanvasStyle) => {
    setElements(next); if (nextStyle) setStyle(nextStyle); markDirty(next, nextStyle);
  }, [markDirty]);

  // ── Fit territory (presentation-only camera; never mutates world coords) ──
  const fit = useCallback(() => {
    const b = territory?.bounds; const box = stageRef.current?.getBoundingClientRect();
    if (!b || !box) return;
    const pad = 60;
    const sx = (box.width - pad * 2) / Math.max(1, b.max_x - b.min_x);
    const sy = (box.height - pad * 2) / Math.max(1, b.max_y - b.min_y);
    const scale = Math.max(0.05, Math.min(sx, sy, 40));
    setView({ scale, tx: pad - b.min_x * scale + (box.width - pad * 2 - (b.max_x - b.min_x) * scale) / 2,
      ty: pad - b.min_y * scale + (box.height - pad * 2 - (b.max_y - b.min_y) * scale) / 2 });
  }, [territory]);
  useEffect(() => { const t = setTimeout(fit, 30); return () => clearTimeout(t); }, [fit]);

  const w2s = useCallback((x: number, y: number) => ({ x: x * view.scale + view.tx, y: y * view.scale + view.ty }), [view]);
  const s2w = useCallback((sx: number, sy: number) => ({ x: (sx - view.tx) / view.scale, y: (sy - view.ty) / view.scale }), [view]);

  // ── Save (manual only — Work Order §41) ──
  const doSave = useCallback(async () => {
    setSaveState("saving"); setSaveErr(null);
    try {
      const saved = await saveCanvas(initial.identity_id, elements, style, initial.authority);
      setElements(normalizeZ(saved.elements)); setStyle(saved.style); setSavedRevision(saved.revision); setSaveState("saved");
      // Re-resolve publication authority so the Publish/Republish button reflects
      // the newly-saved draft immediately (can_publish / draft_changed) — no reload.
      void pub.refresh();
    } catch (e) {
      const err = e as CanvasApiError;
      setSaveState("error");
      if (err.code === "TERRITORY_CHANGED") setSaveErr({ code: "TERRITORY_CHANGED", message: t("editor.canvas.save.territoryChanged") });
      else setSaveErr({ code: err.code ?? "ERROR", message: err.message ?? "" });
      setErrorOpen(true);
    }
  }, [elements, style, initial, pub.refresh, t]);

  // ── Keyboard: Save, Undo/Redo, Delete. NO copy/paste/duplicate (§40). ──
  useEffect(() => {
    if (!isDesktop) return;
    const onKey = (e: KeyboardEvent) => {
      const mod = e.metaKey || e.ctrlKey;
      if (mod && e.key.toLowerCase() === "s") { e.preventDefault(); void doSave(); return; }
      if (mod && e.key.toLowerCase() === "z" && !e.shiftKey) { e.preventDefault(); doUndo(); return; }
      if (mod && (e.key.toLowerCase() === "y" || (e.key.toLowerCase() === "z" && e.shiftKey))) { e.preventDefault(); doRedo(); return; }
      if ((e.key === "Delete" || e.key === "Backspace") && selected.length > 0) {
        const tgt = e.target as HTMLElement;
        if (tgt.tagName === "INPUT" || tgt.tagName === "TEXTAREA") return;
        e.preventDefault(); deleteSelected();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDesktop, selected, doSave, elements, style]);

  const doUndo = useCallback(() => {
    const s = historyRef.current.undo({ elements, style });
    if (s) { setElements(s.elements); setStyle(s.style); setSaveState("dirty"); }
  }, [elements, style]);
  const doRedo = useCallback(() => {
    const s = historyRef.current.redo();
    if (s) { setElements(s.elements); setStyle(s.style); setSaveState("dirty"); }
  }, []);
  const deleteSelected = useCallback(() => {
    commit(normalizeZ(elements.filter((el) => !selected.includes(el.element_id))));
    setSelected([]);
  }, [elements, selected, commit]);

  // ── Unsaved-exit protection (Work Order §42) ──
  useEffect(() => {
    const onBefore = (e: BeforeUnloadEvent) => { if (saveState === "dirty" || saveState === "error") { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", onBefore);
    return () => window.removeEventListener("beforeunload", onBefore);
  }, [saveState]);
  const exitGuard = (href: string) => (e: React.MouseEvent) => {
    if ((saveState === "dirty" || saveState === "error") && !window.confirm(t("editor.canvas.exitConfirm"))) { e.preventDefault(); return; }
    window.location.hash = href;
  };

  // Current viewport centre in WORLD coordinates → new modules land in the
  // visible area at ANY zoom (fixes "nothing appears" past high zoom, §61).
  const viewportCenterWorld = useCallback(() => {
    const box = stageRef.current?.getBoundingClientRect();
    if (!box) return undefined;
    return { x: (box.width / 2 - view.tx) / view.scale, y: (box.height / 2 - view.ty) / view.scale };
  }, [view]);

  // Pan the camera so a WORLD point lands at the viewport centre (keeps scale).
  // Guarantees a freshly added module is ALWAYS visible, even when placement
  // was clamped to an owned parcel far from the current view (§61).
  const centerViewOnWorld = useCallback((wx: number, wy: number) => {
    const box = stageRef.current?.getBoundingClientRect();
    if (!box) return;
    setView((v) => ({ ...v, tx: box.width / 2 - wx * v.scale, ty: box.height / 2 - wy * v.scale }));
  }, []);

  // Zoom around the viewport CENTRE (toolbar +/−): the world point under the
  // centre stays put, so the territory grows/shrinks in place instead of
  // drifting toward the world origin.
  const zoomAtCenter = useCallback((factor: number) => {
    setView((v) => {
      const box = stageRef.current?.getBoundingClientRect();
      const scale = Math.max(0.05, Math.min(40, v.scale * factor));
      if (!box) return { ...v, scale };
      const cx = box.width / 2, cy = box.height / 2;
      const wx = (cx - v.tx) / v.scale, wy = (cy - v.ty) / v.scale;
      return { scale, tx: cx - wx * scale, ty: cy - wy * scale };
    });
  }, []);

  const addModule = (type: ModuleType, subtype?: ShapeSubtype) => {
    if (elements.length >= MAX_ELEMENTS) return;
    // Leaving the pixel art → close its docked studio so the new element's
    // inspector (which shares the right panel) is not hidden behind it.
    setPixelStudioId(null);
    if (type === "IMAGE" || type === "GIF" || type === "VIDEO" || type === "PDF") { setMediaPicker(type); return; }
    const el = makeElement(type, territory, {}, subtype, viewportCenterWorld());
    const next = normalizeZ([...elements, { ...el, z: elements.length }]);
    commit(next); setSelected([el.element_id]);
    const b = elementBBox(el); centerViewOnWorld(b.x + b.w / 2, b.y + b.h / 2);
  };
  const createPixelArt = (dim: number) => {
    setPixelSizePicker(false);
    const el = makeElement("PIXEL_ART", territory, { pixel_art: { w: dim, h: dim, palette: ["#22d3ee", "#f5f7fa", "#ec4899", "#f6c343", "#22c55e", "#05070b"], rle: [[0, dim * dim]] } }, undefined, viewportCenterWorld());
    const next = normalizeZ([...elements, { ...el, z: elements.length }]);
    commit(next); setSelected([el.element_id]);
    const b = elementBBox(el); centerViewOnWorld(b.x + b.w / 2, b.y + b.h / 2);
    setPixelStudioId(el.element_id);
  };
  const createPixelArtBox = (b: Box) => {
    setPixelDrawMode(false);
    const w = Math.max(1, Math.min(PIXEL_ART_MAX_DIM, Math.round(b.w)));
    const h = Math.max(1, Math.min(PIXEL_ART_MAX_DIM, Math.round(b.h)));
    const box = { x: Math.round(b.x), y: Math.round(b.y), w, h };
    const el = makeElement("PIXEL_ART", territory, {
      box,
      pixel_art: { w, h, palette: ["#22d3ee", "#f5f7fa", "#ec4899", "#f6c343", "#22c55e", "#05070b"], rle: [[0, w * h]] },
    }, undefined, viewportCenterWorld());
    const next = normalizeZ([...elements, { ...el, z: elements.length }]);
    commit(next); setSelected([el.element_id]);
    centerViewOnWorld(box.x + w / 2, box.y + h / 2);
    setPixelStudioId(el.element_id);
  };
  const onPickAsset = (asset: MediaAsset) => {
    if (!mediaPicker) return;
    const el = makeElement(mediaPicker, territory, { asset_id: asset.asset_id }, undefined, viewportCenterWorld());
    const next = normalizeZ([...elements, { ...el, z: elements.length }]);
    commit(next); setSelected([el.element_id]); setMediaPicker(null);
    const b = elementBBox(el); centerViewOnWorld(b.x + b.w / 2, b.y + b.h / 2);
  };

  // ── Premium templates (Macro #26 · Increment 3) ──
  // Applying a template REPLACES the local working draft only (elements + global
  // style), marks it dirty via commit(), and re-fits the view. It NEVER touches
  // the backend: Save / Publish stay manual (Work Order §41). The overwrite
  // confirmation lives in TemplatePicker, gated on the current working count so
  // it protects BOTH a loaded saved draft and unsaved local edits.
  const applyTemplate = (def: TemplateDef) => {
    if (!territory || !territory.bounds) { setTemplateOpen(false); return; }
    setPixelStudioId(null);
    const built = buildTemplate(def, territory);
    commit(normalizeZ(built.elements), built.style);
    setSelected([]);
    setTemplateOpen(false);
    setTimeout(fit, 30);
  };

  const patch = (id: string, p: Partial<CanvasElement>) =>
    setElements((prev) => prev.map((el) => (el.element_id === id ? { ...el, ...p } : el)));
  const patchCommit = (id: string, p: Partial<CanvasElement>) => {
    const next = elements.map((el) => (el.element_id === id ? { ...el, ...p } : el));
    commit(next);
  };

  const sel0 = elements.find((e) => e.element_id === selected[0]) ?? null;

  // If a DIFFERENT element becomes selected (inserted or clicked) while the
  // Pixel Studio is docked, close the studio so the new element's inspector —
  // which shares the right panel — is no longer hidden behind it.
  useEffect(() => {
    if (pixelStudioId && selected.length > 0 && selected[0] !== pixelStudioId) {
      setPixelStudioId(null);
    }
  }, [selected, pixelStudioId]);

  // Live GP meter (Work Order §2/§16/§24). Deterministic local mirror of the
  // backend authority; NO backend write during gestures. Backend re-derives and
  // enforces at SAVE (§17/§29). Ratios use the same bbox∩authorized-parcels math
  // the server uses, so the meter matches the persisted authoritative result.
  const gp = useMemo(() => {
    const parcels = territory?.parcels ?? [];
    const area = parcels.reduce((s, p) => s + Math.abs(p.w) * Math.abs(p.h), 0);
    let capacity = 0;
    try { capacity = gridPowerCapacity(area); } catch { capacity = 0; }
    const costHalf: Record<string, number> = {};
    const halves: number[] = [];
    for (const el of elements) {
      let half: number;
      if (el.type === "SHAPE" && el.shape_subtype === "LINE") {
        half = elementCostHalf("SHAPE", "LINE", 0, hasVisibleFootprint(el, docForClip));
      } else if (el.type === "SHAPE" && el.shape_subtype === "ELLIPSE" && el.box) {
        // EXACT rotated-ellipse geometry (mirror of backend ellipse.py). Sorted
        // parcel id → identical iteration order to the server authority.
        const rects = authorizedParcels(el, docForClip)
          .slice()
          .sort((a, b) => a.id - b.id);
        const ratio = ellipseAuthorizedRatio(el.box, el.rotation ?? 0, rects);
        half = elementCostHalf(el.type, el.shape_subtype ?? null, ratio, false);
      } else {
        // Rotation-aware AABB mirrors backend geometry.element_bbox so the meter
        // matches the server authority for rotated box modules.
        const bbox = el.box ? boxAabb(el.box, el.rotation ?? 0) : elementBBox(el);
        const ratio = visibleRatioBBox(bbox, authorizedParcels(el, docForClip));
        half = elementCostHalf(el.type, el.shape_subtype ?? null, ratio, false);
      }
      costHalf[el.element_id] = half;
      halves.push(half);
    }
    return { ...gpMeter(capacity, halves), costHalf };
  }, [territory, elements, docForClip]);

  // Publish derivation (lifted from the old inline PublishPanel) so the Publish
  // BUTTON lives in the toolbar while all status TEXT lives in the inspector.
  const pubState = pub.state;
  const hasPub = !!pubState?.has_active_publication;
  const needsRepublish = hasPub && (pubState?.draft_changed || pubState?.territory_changed);
  const canBackendPublish = !!pubState?.can_publish;
  const publishBlockedDirty = saveState !== "saved";
  const publishDisabled = pub.publishing || pub.status !== "ready" || !canBackendPublish || publishBlockedDirty;
  const publishLabel = pub.publishing ? t("dashboard.publish.publishing") : hasPub ? (needsRepublish ? t("dashboard.publish.republish") : t("dashboard.publish.published")) : t("dashboard.publish.publish");
  const publishBlockedReason = publishBlockedDirty
    ? t("dashboard.publish.blockedSave")
    : !canBackendPublish && pubState?.publish_blocked_reason
    ? pubState.publish_blocked_reason
    : null;
  const doPublish = () => {
    if (publishDisabled) return;
    void pub.publish({
      expected_draft_revision: savedRevision,
      expected_identity_revision: initial.authority.identity_revision,
      expected_membership_fingerprint: initial.authority.membership_fingerprint,
    });
  };

  return (
    <div data-testid="canvas-editor" className="relative w-full h-full flex flex-col bg-obsidian text-text-primary select-none">
      {/* TOP BAR */}
      <div className="flex items-center gap-2 h-12 px-3 border-b border-border-soft bg-panel shrink-0">
        <div className="flex items-center gap-2 min-w-0 flex-1 overflow-x-auto no-scrollbar">
        <a data-testid="canvas-back" href="#dashboard" onClick={exitGuard("#dashboard")} className="text-[11px] font-mono uppercase tracking-[0.16em] text-text-secondary hover:text-text-primary no-underline">← {t("editor.canvas.toolbar.territories")}</a>
        <div className="mx-2 h-5 w-px bg-border-soft" />
        <button data-testid="canvas-undo" disabled={!historyRef.current.canUndo()} onClick={doUndo} className="chip">{t("editor.canvas.toolbar.undo")}</button>
        <button data-testid="canvas-redo" disabled={!historyRef.current.canRedo()} onClick={doRedo} className="chip">{t("editor.canvas.toolbar.redo")}</button>
        <div className="mx-2 h-5 w-px bg-border-soft" />
        <button data-testid="canvas-zoom-out" onClick={() => zoomAtCenter(1 / 1.2)} className="chip">−</button>
        <span data-testid="canvas-zoom" className="text-[11px] font-mono w-12 text-center">{Math.round(view.scale * 100)}%</span>
        <button data-testid="canvas-zoom-in" onClick={() => zoomAtCenter(1.2)} className="chip">+</button>
        <button data-testid="canvas-fit" onClick={fit} className="chip">{t("editor.canvas.toolbar.fit")}</button>
        <div className="mx-2 h-5 w-px bg-border-soft" />
        <GpMeterBar
          usedGp={gp.usedGp}
          capacityGp={gp.capacityGp}
          availableGp={gp.availableGp}
          over={gp.overCapacity}
        />
        <button
          data-testid="canvas-templates-toggle"
          onClick={() => setTemplateOpen(true)}
          disabled={!territory?.bounds}
          title={territory?.bounds ? t("editor.canvas.toolbar.templatesTitle") : t("editor.canvas.toolbar.templatesDisabledTitle")}
          className="chip ml-1 gap-1.5 whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" /><rect x="3" y="14" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" />
          </svg>
          {t("editor.canvas.toolbar.templates")}
        </button>
        <button
          data-testid="canvas-media-library-toggle"
          data-open={libOpen}
          onClick={() => setLibOpen((o) => !o)}
          title={t("editor.canvas.toolbar.mediaLibraryTitle")}
          className={cn("chip ml-1 gap-1.5 whitespace-nowrap", libOpen && "border-border-accent text-neon-teal")}
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="9" cy="9" r="2" /><path d="M21 15l-5-5L5 21" />
          </svg>
          {t("editor.canvas.toolbar.mediaLibrary")}
        </button>
        <a
          data-testid="canvas-artist-profile-link"
          href="#profile"
          onClick={exitGuard("#profile")}
          title={t("editor.canvas.toolbar.artistProfileTitle")}
          className="chip ml-1 gap-1.5 whitespace-nowrap no-underline"
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="8" r="4" /><path d="M4 21v-1a6 6 0 0 1 6-6h4a6 6 0 0 1 6 6v1" />
          </svg>
          {t("editor.canvas.toolbar.artistProfile")}
        </a>
        <a
          data-testid="canvas-3d-editor-link"
          href={`#voxel/${initial.identity_id}`}
          onClick={exitGuard(`#voxel/${initial.identity_id}`)}
          title={t("editor.canvas.toolbar.editor3dTitle")}
          className="chip ml-1 gap-1.5 whitespace-nowrap no-underline"
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 2 3 7v10l9 5 9-5V7z" /><path d="M12 22V12" /><path d="m3 7 9 5 9-5" />
          </svg>
          {t("editor.canvas.toolbar.editor3d")}
        </a>
        </div>
        <div className="flex items-center gap-2 shrink-0 pl-2">
        <button data-testid="canvas-save" onClick={() => void doSave()} disabled={saveState === "saving" || gp.overCapacity} title={gp.overCapacity ? t("editor.canvas.toolbar.saveOverTitle") : t("editor.canvas.toolbar.saveTitle")} className="ml-1 inline-flex items-center h-8 px-4 rounded-md border border-border-strong bg-transparent text-text-primary font-mono text-[11px] uppercase tracking-[0.16em] hover:bg-white/5 disabled:opacity-40 disabled:cursor-not-allowed">{saveState === "saving" ? t("editor.canvas.toolbar.saving") : t("editor.canvas.toolbar.save")}</button>
        <button
          data-testid="publish-button"
          onClick={doPublish}
          disabled={publishDisabled}
          title={publishBlockedReason ?? publishLabel}
          className="inline-flex items-center h-8 px-4 rounded-md border border-neon-teal bg-transparent text-neon-teal font-mono text-[11px] uppercase tracking-[0.16em] hover:bg-neon-teal-soft disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {publishLabel}
        </button>
        {sel0?.type === "PIXEL_ART" && (
          <button
            data-testid="canvas-freeze-button"
            onClick={() => setFreezeSel(sel0)}
            disabled={saveState !== "saved"}
            title={saveState !== "saved" ? t("editor.canvas.toolbar.freezeBlockedTitle") : t("editor.canvas.toolbar.freezeTitle")}
            className="inline-flex items-center h-8 px-4 rounded-md border border-red-300/50 bg-transparent text-red-200 font-mono text-[11px] uppercase tracking-[0.16em] hover:bg-red-400/10 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {t("editor.canvas.toolbar.freeze")}
          </button>
        )}
        </div>
      </div>

      <OwnerModerationBanner
        targetType="territory"
        targetId={initial.identity_id}
        className="mx-3 mt-2 shrink-0"
      />

      <OnboardingContextGuide identityId={initial.identity_id} className="mx-3 mt-2 shrink-0 w-[min(360px,calc(100%-1.5rem))]" />

      {libOpen && <CanvasMediaLibraryPanel onClose={() => setLibOpen(false)} />}
      {templateOpen && territory?.bounds && (
        <TemplatePicker
          existingCount={elements.length}
          onApply={applyTemplate}
          onClose={() => setTemplateOpen(false)}
        />
      )}
      {freezeSel && freezeSel.type === "PIXEL_ART" && freezeSel.pixel_art && (
        <FreezeModal
          identityId={initial.identity_id}
          elementId={freezeSel.element_id}
          sourceW={freezeSel.pixel_art.w}
          sourceH={freezeSel.pixel_art.h}
          parcels={(territory?.parcels ?? []).map((p): FreezeParcel => ({ id: p.id, x: p.x, y: p.y, w: p.w, h: p.h, tier: p.tier }))}
          onClose={() => setFreezeSel(null)}
          onFrozen={(artworkId) => { setFreezeSel(null); window.location.hash = `#artwork/${artworkId}`; }}
        />
      )}
      {placementArtworkId && (
        <div data-testid="canvas-placement-panel" className="fixed right-3 top-16 z-40 max-h-[80vh] w-80 max-w-[92vw] overflow-y-auto">
          <PlacementControls
            artworkId={placementArtworkId}
            onClose={() => setPlacementArtworkId(null)}
            onCommitted={() => frozenArtworks.refetch()}
          />
        </div>
      )}

      <div className="flex-1 flex min-h-0">
        {/* LEFT — tool catalogue */}
        {isDesktop && (
          <div data-testid="canvas-tools" className="w-44 shrink-0 border-r border-border-soft bg-panel overflow-y-auto p-3 space-y-1">
            <ToolLabel>{t("editor.canvas.tools.pixelArt")}</ToolLabel>
            <button data-testid="canvas-add-PIXEL_ART" onClick={() => setPixelSizePicker(true)} className="tool-btn w-full text-left">◧ {t("editor.canvas.enum.module.PIXEL_ART")} <span className="text-neon-teal text-[8px]">{t("editor.canvas.tools.new")}</span></button>
            <ToolLabel>{t("editor.canvas.tools.text")}</ToolLabel>
            {(["TITLE", "TAGLINE", "DESCRIPTION"] as ModuleType[]).map((m) => <Tool key={m} type={m} onClick={() => addModule(m)} />)}
            <ToolLabel>{t("editor.canvas.tools.interactive")}</ToolLabel>
            {(["LINK", "SOCIAL", "CONTACTS"] as ModuleType[]).map((m) => <Tool key={m} type={m} onClick={() => addModule(m)} />)}
            <ToolLabel>{t("editor.canvas.tools.media")}</ToolLabel>
            {(["IMAGE", "GIF", "VIDEO", "PDF"] as ModuleType[]).map((m) => <Tool key={m} type={m} onClick={() => addModule(m)} />)}
            <ToolLabel>{t("editor.canvas.tools.shapes")}</ToolLabel>
            {(["RECTANGLE", "ROUNDED_RECTANGLE", "ELLIPSE", "LINE"] as ShapeSubtype[]).map((s) => (
              <button key={s} data-testid={`canvas-add-shape-${s}`} onClick={() => addModule("SHAPE", s)} className="tool-btn">{t(`editor.canvas.enum.shape.${s}`)}</button>
            ))}
          </div>
        )}

        {/* CENTER — real Territory canvas */}
        <Stage
          stageRef={stageRef} view={view} setView={setView} w2s={w2s} s2w={s2w}
          territory={territory} doc={docForClip} style={style} elements={elements}
          selected={selected} setSelected={setSelected}
          isDesktop={isDesktop} onCommit={commit} onPatch={patch}
          pixelDrawMode={pixelDrawMode} onDrawPixelBox={createPixelArtBox}
          frozenItems={frozenArtworks.items} onEditFrozen={setPlacementArtworkId}
          workArts3d={workArts3d}
          onScene3dDragStart={onScene3dDragStart} onScene3dDrag={onScene3dDrag}
          onScene3dDragEnd={onScene3dDragEnd} onScene3dRotate={rotate3d}
        />

        {pixelDrawMode && (
          <div data-testid="canvas-pixel-draw-banner" className="pointer-events-none fixed top-24 left-1/2 -translate-x-1/2 z-40 flex items-center gap-3 rounded-full border border-neon-teal/60 bg-panel px-4 py-2 shadow-lg">
            <span className="text-[11px] font-mono uppercase tracking-[0.14em] text-neon-teal">{t("editor.canvas.pixelDraw.banner")}</span>
            <button data-testid="canvas-pixel-draw-cancel" onClick={() => setPixelDrawMode(false)} className="pointer-events-auto chip">{t("editor.canvas.cancel")}</button>
          </div>
        )}

        {notice3d && (
          <div data-testid="canvas-3d-notice" className="pointer-events-none fixed top-24 left-1/2 -translate-x-1/2 z-40 flex items-center gap-2 rounded-full border border-cyan-400/60 bg-panel px-4 py-2 shadow-lg">
            <span className="text-[11px] font-mono uppercase tracking-[0.14em] text-cyan-300">{notice3d}</span>
          </div>
        )}

        {/* RIGHT — inspector + layers */}
        {isDesktop && (
          <div className="w-72 shrink-0 border-l border-border-soft bg-panel overflow-y-auto">
            <Inspector el={sel0} style={style} onPatch={patch} onPatchCommit={patchCommit}
              onStyle={(s) => commit(elements, s)} territoryComposition={territory?.composition ?? {}} primary={territory?.primary ?? null}
              saveState={saveState} saveErr={saveErr} onShowError={() => setErrorOpen(true)}
              onReload={saveErr?.code === "TERRITORY_CHANGED" ? onReload : undefined}
              publishStateLabel={pubState?.state ?? null} publishBlockedReason={publishBlockedReason}
              publishActionError={pub.actionError}
              selectedGp={sel0 ? { used: halfToGp(gp.costHalf[sel0.element_id] ?? 0), max: halfToGp(moduleBaseHalf(sel0.type, sel0.shape_subtype ?? null)) } : null}
              onPickPdfBg={(id) => setPdfBgTarget(id)}
              onEditPixels={(id) => setPixelStudioId(id)} />
            <Layers elements={elements} selected={selected} setSelected={setSelected}
              onReorder={(next) => commit(next)} onToggle={(id, k) => {
                const el = elements.find((e) => e.element_id === id); if (!el) return;
                patchCommit(id, { [k]: !el[k] } as Partial<CanvasElement>);
              }} />
          </div>
        )}
      </div>

      {!isDesktop && (
        <div data-testid="canvas-mobile-readonly" className="px-4 py-2 text-[11px] font-mono text-text-muted border-t border-border-soft">
          {t("editor.canvas.mobileReadonly")}
        </div>
      )}

      {mediaPicker && (
        <MediaPicker moduleType={mediaPicker} onPick={onPickAsset} onClose={() => setMediaPicker(null)} />
      )}
      {pdfBgTarget && (
        <MediaPicker moduleType="IMAGE" onPick={(a) => { patchCommit(pdfBgTarget, { pdf_bg_asset_id: a.asset_id }); setPdfBgTarget(null); }} onClose={() => setPdfBgTarget(null)} />
      )}
      {pixelSizePicker && (
        <div data-testid="pixel-size-picker" className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-6" onClick={() => setPixelSizePicker(false)}>
          <div className="rounded-lg border border-border-soft bg-panel p-5 w-[min(420px,95%)]" onClick={(e) => e.stopPropagation()}>
            <div className="font-mono text-[12px] uppercase tracking-[0.16em] mb-1">{t("editor.canvas.pixelSize.title")}</div>
            <div className="text-[11px] text-text-muted mb-3">{t("editor.canvas.pixelSize.body")}</div>
            <div className="grid grid-cols-2 gap-2">
              {PIXEL_ART_SIZE_PRESETS.map((d) => (
                <button key={d} data-testid={`pixel-size-${d}`} onClick={() => createPixelArt(d)} className="chip">{d}×{d}</button>
              ))}
            </div>
            <button data-testid="pixel-size-custom" onClick={() => { setPixelSizePicker(false); setPixelDrawMode(true); }}
              className="mt-2 w-full flex items-center justify-center gap-2 px-3 py-2 rounded-md border border-neon-teal/60 text-neon-teal text-[11px] font-mono uppercase tracking-[0.14em] hover:bg-neon-teal-soft">
              ✏ {t("editor.canvas.pixelSize.custom")}
            </button>
          </div>
        </div>
      )}
      {pixelStudioId && (() => {
        const pel = elements.find((e) => e.element_id === pixelStudioId);
        return pel && pel.type === "PIXEL_ART" && pel.pixel_art
          ? <PixelStudio el={pel} onPatch={patch} onPatchCommit={patchCommit}
              onClose={() => setPixelStudioId(null)}
              onSaveCanvas={doSave}
              onDelete={() => { commit(normalizeZ(elements.filter((e) => e.element_id !== pixelStudioId))); setSelected([]); setPixelStudioId(null); }} />
          : null;
      })()}

      {errorOpen && saveErr && (
        <SaveErrorModal
          code={saveErr.code}
          detail={saveErr.message}
          onClose={() => setErrorOpen(false)}
          onReload={saveErr.code === "TERRITORY_CHANGED" ? () => { setErrorOpen(false); onReload(); } : undefined}
        />
      )}
    </div>
  );
}

function ToolLabel({ children }: { children: React.ReactNode }) {
  return <div className="pt-2 text-[9px] font-mono uppercase tracking-[0.2em] text-text-muted">{children}</div>;
}
function Tool({ type, onClick }: { type: ModuleType; onClick: () => void }) {
  const t = useT();
  return <button data-testid={`canvas-add-${type}`} onClick={onClick} className="tool-btn">{t(`editor.canvas.enum.module.${type}`)}</button>;
}

// ─────────────────────────── Stage ───────────────────────────
function Stage(props: {
  stageRef: React.RefObject<HTMLDivElement>; view: View; setView: (f: (v: View) => View) => void;
  w2s: (x: number, y: number) => { x: number; y: number }; s2w: (x: number, y: number) => { x: number; y: number };
  territory?: CanvasDoc["territory"]; doc: CanvasDoc; style: CanvasStyle; elements: CanvasElement[];
  selected: string[]; setSelected: (ids: string[]) => void; isDesktop: boolean;
  onCommit: (next: CanvasElement[]) => void; onPatch: (id: string, p: Partial<CanvasElement>) => void;
  pixelDrawMode?: boolean; onDrawPixelBox?: (b: Box) => void;
  frozenItems?: PlacedFrozenArtwork[]; onEditFrozen?: (id: string) => void;
  workArts3d?: WorkArt[];
  onScene3dDragStart?: () => void;
  onScene3dDrag?: (dx: number, dy: number) => void;
  onScene3dDragEnd?: () => void;
  onScene3dRotate?: () => void;
}) {
  const { stageRef, view, setView, s2w, territory, doc, style, elements, selected, setSelected, isDesktop, onCommit, onPatch } = props;
  const parcels = territory?.parcels ?? [];
  const dragRef = useRef<null | { mode: string; id: string; startW: { x: number; y: number }; orig: CanvasElement }>(null);
  const panRef = useRef<null | { sx: number; sy: number; tx: number; ty: number }>(null);
  const marqueeRef = useRef<null | { start: { x: number; y: number } }>(null);
  const [marquee, setMarquee] = useState<null | Box>(null);

  const onWheel = (e: React.WheelEvent) => {
    if (!(e.ctrlKey || e.metaKey)) return; // page scroll otherwise (Work Order §63)
    e.preventDefault();
    const rect = stageRef.current!.getBoundingClientRect();
    const px = e.clientX - rect.left, py = e.clientY - rect.top;
    const before = s2w(px, py);
    setView((v) => {
      const scale = Math.max(0.05, Math.min(40, v.scale * (e.deltaY < 0 ? 1.1 : 1 / 1.1)));
      return { scale, tx: px - before.x * scale, ty: py - before.y * scale };
    });
  };

  const onStageDown = (e: React.PointerEvent) => {
    if (e.target === e.currentTarget || (e.target as HTMLElement).dataset.stagebg) {
      const rect = stageRef.current!.getBoundingClientRect();
      if (props.pixelDrawMode) {
        marqueeRef.current = { start: s2w(e.clientX - rect.left, e.clientY - rect.top) };
        setMarquee({ x: marqueeRef.current.start.x, y: marqueeRef.current.start.y, w: 0, h: 0 });
        (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
        return;
      }
      setSelected([]);
      panRef.current = { sx: e.clientX, sy: e.clientY, tx: view.tx, ty: view.ty };
      (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    }
  };
  const onMove = (e: React.PointerEvent) => {
    if (marqueeRef.current) {
      const rect = stageRef.current!.getBoundingClientRect();
      const cur = s2w(e.clientX - rect.left, e.clientY - rect.top);
      const st = marqueeRef.current.start;
      setMarquee({ x: Math.min(st.x, cur.x), y: Math.min(st.y, cur.y), w: Math.abs(cur.x - st.x), h: Math.abs(cur.y - st.y) });
      return;
    }
    if (panRef.current) {
      const dx = e.clientX - panRef.current.sx, dy = e.clientY - panRef.current.sy;
      setView((v) => ({ ...v, tx: panRef.current!.tx + dx, ty: panRef.current!.ty + dy }));
      return;
    }
    const d = dragRef.current;
    if (!d) return;
    const rect = stageRef.current!.getBoundingClientRect();
    const cur = s2w(e.clientX - rect.left, e.clientY - rect.top);
    const start = d.startW;
    const dxw = cur.x - start.x, dyw = cur.y - start.y;
    const snapT = 8 / view.scale;
    const edges = { xs: [] as number[], ys: [] as number[] };
    for (const p of parcels) { edges.xs.push(p.x, p.x + p.w); edges.ys.push(p.y, p.y + p.h); }
    for (const el of elements) { if (el.element_id === d.id || !el.box) continue; edges.xs.push(el.box.x, el.box.x + el.box.w); edges.ys.push(el.box.y, el.box.y + el.box.h); }
    if (d.mode === "move" && d.orig.box) {
      let nx = d.orig.box.x + dxw, ny = d.orig.box.y + dyw;
      nx = snapValue(nx, edges.xs, snapT); ny = snapValue(ny, edges.ys, snapT);
      onPatch(d.id, { box: { ...d.orig.box, x: nx, y: ny } });
    } else if (d.mode === "resize-se" && d.orig.box) {
      onPatch(d.id, { box: { ...d.orig.box, w: Math.max(0.5, d.orig.box.w + dxw), h: Math.max(0.5, d.orig.box.h + dyw) } });
    } else if (d.mode === "rotate" && d.orig.box) {
      const cx = d.orig.box.x + d.orig.box.w / 2, cy = d.orig.box.y + d.orig.box.h / 2;
      let deg = Math.atan2(cur.y - cy, cur.x - cx) * 180 / Math.PI + 90;
      if (e.shiftKey) deg = Math.round(deg / 15) * 15;
      onPatch(d.id, { rotation: Math.max(-360, Math.min(360, deg)) });
    } else if (d.mode === "p1" && d.orig.line) {
      onPatch(d.id, { line: { ...d.orig.line, x1: cur.x, y1: cur.y } });
    } else if (d.mode === "p2" && d.orig.line) {
      onPatch(d.id, { line: { ...d.orig.line, x2: cur.x, y2: cur.y } });
    }
  };
  const onUp = (e: React.PointerEvent) => {
    // Always release any capture taken on drag/pan start (see startDrag / onStageDown).
    try { stageRef.current?.releasePointerCapture(e.pointerId); } catch { /* not captured */ }
    if (marqueeRef.current) {
      const m = marquee; marqueeRef.current = null; setMarquee(null);
      if (m && Math.round(m.w) >= 1 && Math.round(m.h) >= 1) props.onDrawPixelBox?.(m);
      return;
    }
    if (panRef.current) { panRef.current = null; return; }
    const d = dragRef.current; dragRef.current = null;
    if (!d) return;
    // Drag-end validity (Work Order §25): if no visible intersection, restore.
    const el = elements.find((x) => x.element_id === d.id);
    if (el && !intersectsTerritory(elementBBox(el), territory)) { onPatch(d.id, d.orig); onCommit(elements.map((x) => x.element_id === d.id ? d.orig : x)); return; }
    onCommit(elements); // NO backend write — local commit + history
    void e;
  };

  const startDrag = (mode: string, el: CanvasElement) => (e: React.PointerEvent) => {
    if (el.locked) return;
    e.stopPropagation();
    const rect = stageRef.current!.getBoundingClientRect();
    dragRef.current = { mode, id: el.element_id, startW: s2w(e.clientX - rect.left, e.clientY - rect.top), orig: JSON.parse(JSON.stringify(el)) };
    // Capture the pointer on the STAGE so move/up keep firing even when the
    // cursor leaves the canvas area. Without this, a resize/move dragged outside
    // the stage silently stops tracking and never commits — it feels like the
    // resize "resets" (Claudio: "se ridimensiono fuori dal canvas mi riazzera").
    try { stageRef.current?.setPointerCapture(e.pointerId); } catch { /* older browsers */ }
    if (!e.shiftKey) setSelected([el.element_id]);
    else setSelected(selected.includes(el.element_id) ? selected.filter((x) => x !== el.element_id) : [...selected, el.element_id]);
  };

  const bg = style.background_mode === "SOLID_COLOR" ? hexOf(style.background_color, "#0d1117") : "transparent";
  const perim = hexOf(style.perimeter_color, "#22d3ee");
  // Perimeter geometry = the screen-space bounding box of the owned parcel union.
  // The stroke sits ON the territory boundary (clipped to the exact union), so it
  // is ALWAYS visible around the parcels — not only when the territory happens to
  // overflow the viewport edges.
  const pbx = parcels.length
    ? (() => {
        const x0 = Math.min(...parcels.map((p) => p.x));
        const y0 = Math.min(...parcels.map((p) => p.y));
        const x1 = Math.max(...parcels.map((p) => p.x + p.w));
        const y1 = Math.max(...parcels.map((p) => p.y + p.h));
        const tl = props.w2s(x0, y0);
        return { x: tl.x, y: tl.y, w: (x1 - x0) * view.scale, h: (y1 - y0) * view.scale };
      })()
    : null;

  return (
    <div ref={stageRef} data-testid="canvas-stage" onWheel={onWheel} onPointerDown={onStageDown} onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={onUp}
      className="flex-1 relative overflow-hidden bg-[#06080d]" style={{ touchAction: "none", cursor: props.pixelDrawMode ? "crosshair" : undefined }}>
      <div data-stagebg="1" className="absolute inset-0" />
      <svg className="absolute inset-0 w-full h-full pointer-events-none">
        <defs>
          <clipPath id="territory-clip">
            {parcels.map((p) => { const s = props.w2s(p.x, p.y); return <rect key={p.id} x={s.x} y={s.y} width={p.w * view.scale} height={p.h * view.scale} />; })}
          </clipPath>
        </defs>
        {/* Territory background (solid color across owned union only) */}
        {bg !== "transparent" && <g clipPath="url(#territory-clip)"><rect x={0} y={0} width="100%" height="100%" fill={bg} /></g>}
        {/* CONTENT — clipped to EXACT ownership union (Work Order §24) */}
        <g clipPath="url(#territory-clip)" data-testid="canvas-content-layer">
          {[...elements].sort((a, b) => a.z - b.z).filter((e) => !e.hidden).map((el) => <ElementView key={el.element_id} el={el} doc={doc} w2s={props.w2s} scale={view.scale} />)}
        </g>
        {/* PARCEL GRID OVERLAY — ALWAYS above content (Work Order §54) */}
        <g data-testid="canvas-parcel-grid">
          {parcels.map((p) => { const s = props.w2s(p.x, p.y); const tier = p.tier as Tier; return (
            <rect key={p.id} x={s.x} y={s.y} width={p.w * view.scale} height={p.h * view.scale}
              fill="none" stroke={TIER_COLORS[tier]} strokeWidth={tier === "GRID_ZERO" ? 2.4 : 1.2} strokeOpacity={0.75}
              style={tier === "GRID_ZERO" ? { filter: "drop-shadow(0 0 4px #8b5cf6)" } : undefined} />
          ); })}
        </g>
        {/* TERRITORY PERIMETER (system geometry; user picks color/glow only) */}
        {pbx && <g clipPath="url(#territory-clip)"><rect x={pbx.x} y={pbx.y} width={pbx.w} height={pbx.h} fill="none" stroke={perim} strokeWidth={4}
          style={{ filter: GLOW_PX[style.perimeter_glow] ? `drop-shadow(0 0 ${GLOW_PX[style.perimeter_glow]}px ${perim})` : undefined }} /></g>}
      </svg>

      {/* Selection affordances (may render above the clip — Work Order §24).
          MACRO #31 — rendered on ALL devices (previously desktop-only) so
          resize/rotate/line handles are usable on touch; hit targets enlarged
          in the Handle component below. */}
      {elements.filter((e) => selected.includes(e.element_id)).map((el) => (
        <Handles key={el.element_id} el={el} w2s={props.w2s} scale={view.scale} startDrag={startDrag} />
      ))}
      {/* Interaction proxies for elements (hit-test) */}
      {elements.filter((e) => !e.hidden).map((el) => (
        <HitProxy key={el.element_id} el={el} w2s={props.w2s} scale={view.scale} selected={selected.includes(el.element_id)} onDown={startDrag("move", el)} />
      ))}
      {/* Read-only overlay of the owner's FROZEN artworks placed on this land.
          Click to edit placement (drag to move). */}
      <CanvasFrozenArtworkLayer
        items={props.frozenItems ?? []}
        w2s={props.w2s}
        scale={view.scale}
        onEdit={(id) => props.onEditFrozen?.(id)}
      />
      {/* Owner's published 3D scene, projected top-down onto the Canvas and
          draggable to move the REAL 3D placement (see Canvas3DFootprintLayer). */}
      <Canvas3DFootprintLayer
        workArts={props.workArts3d ?? []}
        w2s={props.w2s}
        scale={view.scale}
        interactive={isDesktop}
        onDragStart={props.onScene3dDragStart}
        onDrag={props.onScene3dDrag}
        onDragEnd={props.onScene3dDragEnd}
        onRotate={props.onScene3dRotate}
      />
      {/* Custom pixel-art size marquee (draw a rectangle to define W×H) */}
      {marquee && (() => {
        const s = props.w2s(marquee.x, marquee.y);
        const w = marquee.w * view.scale, h = marquee.h * view.scale;
        return (
          <div data-testid="pixel-draw-marquee" className="absolute pointer-events-none border-2 border-neon-teal bg-neon-teal/10"
            style={{ left: s.x, top: s.y, width: w, height: h }}>
            <div className="absolute -top-6 left-0 whitespace-nowrap rounded-sm bg-neon-teal px-1.5 py-0.5 text-[10px] font-mono text-obsidian">
              {Math.max(1, Math.round(marquee.w))}×{Math.max(1, Math.round(marquee.h))}
            </div>
          </div>
        );
      })()}
    </div>
  );
}

function ElementView({ el, doc, w2s, scale }: { el: CanvasElement; doc: CanvasDoc; w2s: (x: number, y: number) => { x: number; y: number }; scale: number }) {
  void doc;
  if (el.line) {
    const a = w2s(el.line.x1, el.line.y1), b = w2s(el.line.x2, el.line.y2);
    return <line x1={a.x} y1={a.y} x2={b.x} y2={b.y} stroke={hexOf(el.line_color, "#f5f7fa")} strokeWidth={2.5} opacity={el.opacity} strokeLinecap="round" />;
  }
  if (!el.box) return null;
  const s = w2s(el.box.x, el.box.y);
  const cx = s.x + (el.box.w * scale) / 2, cy = s.y + (el.box.h * scale) / 2;
  const transform = el.rotation ? `rotate(${el.rotation} ${cx} ${cy})` : undefined;
  const rx = el.radius === "PILL" ? el.box.h * scale / 2 : el.radius === "ROUNDED" ? 10 : el.radius === "SLIGHT" ? 4 : 0;
  const glow = GLOW_PX[el.glow] ? `drop-shadow(0 0 ${GLOW_PX[el.glow]}px ${hexOf(el.glow_color, "#22d3ee")})` : undefined;
  const common = { transform, opacity: el.opacity, style: { filter: glow } as React.CSSProperties };
  if (el.type === "SHAPE" && el.shape_subtype === "ELLIPSE") {
    return <ellipse cx={cx} cy={cy} rx={(el.box.w * scale) / 2} ry={(el.box.h * scale) / 2} fill={hexOf(el.fill_color)} {...common} />;
  }
  if (el.type === "SHAPE") {
    return <rect x={s.x} y={s.y} width={el.box.w * scale} height={el.box.h * scale} rx={rx} fill={hexOf(el.fill_color)} {...common} />;
  }
  if (el.type === "IMAGE" || el.type === "GIF") {
    const fit = String(el.present?.fit ?? "COVER") === "CONTAIN" ? "contain" : "cover";
    const fx = Number(el.present?.focal_x ?? 0.5) * 100;
    const fy = Number(el.present?.focal_y ?? 0.5) * 100;
    return (
      <foreignObject x={s.x} y={s.y} width={Math.max(1, el.box.w * scale)} height={Math.max(1, el.box.h * scale)} {...common}>
        <div style={{ width: "100%", height: "100%", overflow: "hidden", borderRadius: rx, background: "#0d1117" }}>
          <img src={assetContentUrl(el.asset_id!)} alt="" draggable={false}
            style={{ width: "100%", height: "100%", objectFit: fit as "cover" | "contain", objectPosition: `${fx}% ${fy}%`, display: "block", pointerEvents: "none" }} />
        </div>
      </foreignObject>
    );
  }
  if (el.type === "PDF") {
    const bw = Math.max(1, el.box.w * scale), bh = Math.max(1, el.box.h * scale);
    const bgUrl = el.pdf_bg_asset_id ? assetContentUrl(el.pdf_bg_asset_id) : undefined;
    return (<g {...common}>
      <foreignObject x={s.x} y={s.y} width={bw} height={bh}>
        <FitToBox boxW={bw} boxH={bh}>
          <PdfCard title={el.title} scale={1} interactive={false} bgImageUrl={bgUrl} />
        </FitToBox>
      </foreignObject>
    </g>);
  }
  if (el.type === "VIDEO") {
    const bw = Math.max(1, el.box.w * scale), bh = Math.max(1, el.box.h * scale);
    return (<g {...common}>
      <rect x={s.x} y={s.y} width={bw} height={bh} rx={rx} fill="#141b28" stroke="#2b3340" />
      <foreignObject x={s.x} y={s.y} width={bw} height={bh}>
        <AutoFitText text="▶ VIDEO" maxFontPx={bh * 0.9} font={FONT_STACKS.GRID_SANS} color="#8a97a8" bold={false} italic={false} underline={false} align="CENTER" lineHeight={1.15} />
      </foreignObject>
    </g>);
  }
  if (el.type === "PIXEL_ART" && el.pixel_art) {
    const bw = Math.max(1, el.box.w * scale), bh = Math.max(1, el.box.h * scale);
    return (
      <foreignObject x={s.x} y={s.y} width={bw} height={bh} {...common}>
        <PixelArtRender pixelArt={el.pixel_art} boxW={bw} boxH={bh} />
      </foreignObject>
    );
  }
  if (el.type === "SOCIAL") {
    const ss = socialStyleWithDefaults(el.social_style);
    const bw = Math.max(1, el.box.w * scale), bh = Math.max(1, el.box.h * scale);
    const ax = ss.align === "START" ? "flex-start" : ss.align === "END" ? "flex-end" : "center";
    return (
      <foreignObject x={s.x} y={s.y} width={bw} height={bh} {...common}>
        <FitToBox boxW={bw} boxH={bh} alignX={ax}>
          <SocialRender destinations={el.destinations ?? []} style={ss} scale={1} interactive={false} />
        </FitToBox>
      </foreignObject>
    );
  }
  if (el.type === "CONTACTS") {
    const cs = contactsStyleWithDefaults(el.contacts_style);
    const bw = Math.max(1, el.box.w * scale), bh = Math.max(1, el.box.h * scale);
    const ax = cs.align === "START" ? "flex-start" : cs.align === "END" ? "flex-end" : "center";
    return (
      <foreignObject x={s.x} y={s.y} width={bw} height={bh} {...common}>
        <FitToBox boxW={bw} boxH={bh} alignX={ax}>
          <ContactsRender entries={el.entries ?? []} style={cs} scale={1} interactive={false} />
        </FitToBox>
      </foreignObject>
    );
  }
  if (el.type === "LINK") {
    const ls = linkStyleWithDefaults(el.link_style);
    const bw = Math.max(1, el.box.w * scale), bh = Math.max(1, el.box.h * scale);
    const ax = ls.align === "START" ? "flex-start" : ls.align === "END" ? "flex-end" : "center";
    return (
      <foreignObject x={s.x} y={s.y} width={bw} height={bh} {...common}>
        <FitToBox boxW={bw} boxH={bh} alignX={ax}>
          <LinkRender label={el.label ?? ""} subtitle={el.subtitle} url={el.url} style={ls} scale={1} interactive={false} />
        </FitToBox>
      </foreignObject>
    );
  }
  // text (TITLE/TAGLINE/DESCRIPTION) → auto-fitting foreignObject text (§12).
  // SOCIAL / CONTACTS / LINK are handled above via shared icon/CTA renderers.
  const ts = (el.text_style ?? {}) as Record<string, unknown>;
  const font = FONT_STACKS[(ts.font as string) ?? "GRID_SANS"] ?? FONT_STACKS.GRID_SANS;
  const label = el.text;
  const color = hexOf((ts.color as string) ?? "", "#f5f7fa");
  const align = (ts.align as string) ?? "LEFT";
  return (
    <foreignObject x={s.x} y={s.y} width={Math.max(1, el.box.w * scale)} height={Math.max(1, el.box.h * scale)} {...common}>
      <AutoFitText text={String(label ?? "")} maxFontPx={((ts.font_size as number) ?? 16) * scale}
        font={font} color={color} bold={Boolean(ts.bold)} italic={Boolean(ts.italic)} underline={Boolean(ts.underline)}
        align={align} lineHeight={(ts.line_height as number) ?? 1.2} />
    </foreignObject>
  );
}

function HitProxy({ el, w2s, scale, selected, onDown }: { el: CanvasElement; w2s: (x: number, y: number) => { x: number; y: number }; scale: number; selected: boolean; onDown: (e: React.PointerEvent) => void }) {
  const b = elementBBox(el); const s = w2s(b.x, b.y);
  return <div data-testid={`canvas-el-${el.element_id}`} data-selected={selected} onPointerDown={onDown}
    style={{ position: "absolute", left: s.x, top: s.y, width: Math.max(6, b.w * scale), height: Math.max(6, b.h * scale), cursor: el.locked ? "not-allowed" : "move", outline: selected ? "1.5px solid #22d3ee" : "none" }} />;
}

function Handles({ el, w2s, scale, startDrag }: { el: CanvasElement; w2s: (x: number, y: number) => { x: number; y: number }; scale: number; startDrag: (mode: string, el: CanvasElement) => (e: React.PointerEvent) => void }) {
  if (el.line) {
    const a = w2s(el.line.x1, el.line.y1), b = w2s(el.line.x2, el.line.y2);
    return (<>
      <Handle testid="canvas-line-p1" x={a.x} y={a.y} onDown={startDrag("p1", el)} />
      <Handle testid="canvas-line-p2" x={b.x} y={b.y} onDown={startDrag("p2", el)} />
    </>);
  }
  if (!el.box) return null;
  // Pixel art: 1 cell == 1 parcel, so its box size is fixed by the grid — from
  // the canvas you can only MOVE the slot. No resize/rotate handles (resizing
  // would stretch/squash the pixels). Dimensions change only in Pixel Studio.
  if (el.type === "PIXEL_ART") return null;
  const s = w2s(el.box.x, el.box.y); const w = el.box.w * scale, h = el.box.h * scale;
  return (<>
    <Handle testid="canvas-resize-se" x={s.x + w} y={s.y + h} onDown={startDrag("resize-se", el)} />
    <Handle testid="canvas-rotate" x={s.x + w / 2} y={s.y - 24} onDown={startDrag("rotate", el)} color="#f6c343" />
  </>);
}
function Handle({ x, y, onDown, testid, color = "#22d3ee" }: { x: number; y: number; onDown: (e: React.PointerEvent) => void; testid: string; color?: string }) {
  // MACRO #31 — 28px transparent touch hit area with a centered 14px visible
  // dot so resize/rotate/line handles are comfortably tappable on touch while
  // staying visually compact on desktop.
  const HIT = 28, DOT = 14;
  return (
    <div data-testid={testid} onPointerDown={onDown}
      style={{ position: "absolute", left: x - HIT / 2, top: y - HIT / 2, width: HIT, height: HIT, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", touchAction: "none", zIndex: 20 }}>
      <span style={{ width: DOT, height: DOT, borderRadius: 4, background: color, border: "1px solid #06080d", boxShadow: "0 0 0 1px rgba(0,0,0,0.45)" }} />
    </div>
  );
}

// ─────────────────────────── Inspector ───────────────────────────
function Inspector(props: {
  el: CanvasElement | null; style: CanvasStyle; onPatch: (id: string, p: Partial<CanvasElement>) => void;
  onPatchCommit: (id: string, p: Partial<CanvasElement>) => void; onStyle: (s: CanvasStyle) => void;
  territoryComposition: Record<string, number>; primary: string | null;
  saveState: SaveState; saveErr: { code: string; message: string } | null; onShowError: () => void; onReload?: () => void;
  publishStateLabel: string | null; publishBlockedReason: string | null;
  publishActionError: { code: string; message: string } | null;
  selectedGp: { used: number; max: number } | null;
  onPickPdfBg: (elementId: string) => void;
  onEditPixels: (elementId: string) => void;
}) {
  const {
    el, style, onPatch, onPatchCommit, onStyle, territoryComposition, primary,
    saveState, saveErr, onShowError, onReload, publishStateLabel, publishBlockedReason, publishActionError, selectedGp, onPickPdfBg, onEditPixels,
  } = props;
  const t = useT();
  return (
    <div data-testid="canvas-inspector" className="p-3 border-b border-border-soft space-y-3">
      <div className="text-[9px] font-mono uppercase tracking-[0.2em] text-text-muted">{t("editor.canvas.inspector.territoryTiers")}</div>
      <div data-testid="canvas-tier-composition" className="flex flex-wrap gap-1">
        <span data-testid="canvas-tier-primary" className="text-[10px] font-mono px-2 py-0.5 rounded-sm border border-border-accent text-neon-teal">{primary ? t(`editor.canvas.tier.${primary}`) : "—"}</span>
        {Object.entries(territoryComposition).map(([tk, c]) => <span key={tk} className="text-[10px] font-mono px-2 py-0.5 rounded-sm border border-border-soft text-text-secondary">{t(`editor.canvas.tier.${tk}`)}: {c}</span>)}
      </div>

      {/* Save / Publish status — relocated here from the toolbar (keeps the top
          bar to controls only; status reads logically beside the tiers). */}
      <div data-testid="canvas-status" className="rounded-md border border-border-soft bg-obsidian/60 p-2.5 space-y-1.5">
        <div className="flex items-center justify-between gap-2">
          <span className="flex items-center gap-2">
            <span aria-hidden className={cn("h-1.5 w-1.5 rounded-full",
              saveState === "saved" && "bg-state-success", saveState === "dirty" && "bg-neon-teal",
              saveState === "saving" && "bg-text-muted", saveState === "error" && "bg-state-danger")} />
            <span data-testid="canvas-save-state" data-state={saveState} className={cn("text-[10px] font-mono uppercase tracking-[0.16em]",
              saveState === "saved" && "text-state-success", saveState === "dirty" && "text-neon-teal",
              saveState === "saving" && "text-text-secondary", saveState === "error" && "text-state-danger")}>
              {saveState === "saved" ? t("editor.canvas.status.saved") : saveState === "dirty" ? t("editor.canvas.status.unsavedChanges") : saveState === "saving" ? t("editor.canvas.status.saving") : t("editor.canvas.status.error")}
            </span>
          </span>
          {publishStateLabel && (
            <span data-testid="canvas-publish-state" className="text-[9px] font-mono uppercase tracking-[0.16em] text-text-muted">
              {t(`dashboard.publish.stateLabels.${publishStateLabel}`)}
            </span>
          )}
        </div>
        {saveErr && (
          <button data-testid="canvas-save-error" onClick={onShowError} title={t("editor.canvas.status.showError")}
            className="block w-full text-left text-[10px] text-state-danger font-mono truncate underline decoration-dotted underline-offset-2 hover:opacity-80">
            {saveErr.code === "TERRITORY_CHANGED" ? saveErr.message : `${saveErr.code}: ${saveErr.message}`}
          </button>
        )}
        {onReload && (
          <button data-testid="canvas-reload" onClick={onReload} className="chip h-7">{t("editor.error.reload")}</button>
        )}
        {publishBlockedReason && (
          <div data-testid="canvas-publish-blocked" className="text-[10px] font-mono text-neon-teal">{publishBlockedReason}</div>
        )}
        {publishActionError && (
          <div data-testid="canvas-publish-error" className="text-[10px] font-mono text-state-danger">{publishActionError.code}</div>
        )}
      </div>
      {!el ? (
        <div className="text-[11px] text-text-muted font-mono pt-2">{t("editor.canvas.inspector.globalStyle")}</div>
      ) : (
        <div className="flex items-center justify-between gap-2 pt-2">
          <div className="text-[9px] font-mono uppercase tracking-[0.2em] text-text-muted">{t(`editor.canvas.enum.module.${el.type}`)}{el.shape_subtype ? ` · ${t(`editor.canvas.enum.shape.${el.shape_subtype}`)}` : ""}</div>
          {selectedGp && (
            <span data-testid="canvas-selected-gp" className="text-[10px] font-mono text-text-secondary whitespace-nowrap">{t("editor.canvas.inspector.selectedGp", { used: selectedGp.used, max: selectedGp.max })}</span>
          )}
        </div>
      )}
      {!el && (
        <div className="space-y-2">
          <Select testid="canvas-bg-mode" label={t("editor.canvas.inspector.background")} value={style.background_mode} options={["NEUTRAL", "SOLID_COLOR"]} optLabel={(o) => t(`editor.canvas.enum.bgMode.${o}`)} onChange={(v) => onStyle({ ...style, background_mode: v as CanvasStyle["background_mode"] })} />
          {style.background_mode === "SOLID_COLOR" && <ColorSelect testid="canvas-bg-color" label={t("editor.canvas.inspector.bgColor")} value={style.background_color ?? "GRID_OBSIDIAN"} onChange={(v) => onStyle({ ...style, background_color: v })} />}
          <ColorSelect testid="canvas-perimeter-color" label={t("editor.canvas.inspector.perimeter")} value={style.perimeter_color} onChange={(v) => onStyle({ ...style, perimeter_color: v })} />
          <Select testid="canvas-perimeter-glow" label={t("editor.canvas.inspector.perimeterGlow")} value={style.perimeter_glow} options={["OFF", "LOW", "MEDIUM", "HIGH"]} optLabel={(o) => t(`editor.canvas.enum.glow.${o}`)} onChange={(v) => onStyle({ ...style, perimeter_glow: v as CanvasStyle["perimeter_glow"] })} />
        </div>
      )}
      {el && (
        <div className="space-y-2">
          <Range testid="canvas-opacity" label={t("editor.canvas.inspector.opacity", { v: Math.round(el.opacity * 100) })} value={el.opacity} onChange={(v) => onPatchCommit(el.element_id, { opacity: v })} />
          <Select testid="canvas-glow" label={t("editor.canvas.inspector.glow")} value={el.glow} options={["OFF", "LOW", "MEDIUM", "HIGH"]} optLabel={(o) => t(`editor.canvas.enum.glow.${o}`)} onChange={(v) => onPatchCommit(el.element_id, { glow: v as CanvasElement["glow"] })} />
          {el.glow !== "OFF" && (
            <ColorSelect testid="canvas-glow-color" label={t("editor.canvas.inspector.glowColor")} value={el.glow_color ?? "GRID_CYAN"} onChange={(v) => onPatchCommit(el.element_id, { glow_color: v })} />
          )}
          {(el.type === "TITLE" || el.type === "TAGLINE" || el.type === "DESCRIPTION") && (
            <>
              <label className="block text-[10px] font-mono text-text-muted">{t("editor.canvas.inspector.text")}</label>
              <textarea data-testid="canvas-text-input" value={el.text ?? ""}
                onChange={(e) => onPatch(el.element_id, { text: e.target.value })}
                onBlur={(e) => onPatchCommit(el.element_id, { text: e.target.value })}
                className="w-full h-16 bg-obsidian border border-border-soft rounded-sm p-2 text-[12px] text-text-primary" />
              <FontControls el={el} onPatchCommit={onPatchCommit} />
            </>
          )}
          {el.type === "LINK" && (<>
            <TextField testid="canvas-link-label" label={t("editor.canvas.inspector.label")} value={el.label ?? ""} onLive={(v) => onPatch(el.element_id, { label: v })} onCommit={(v) => onPatchCommit(el.element_id, { label: v })} />
            <TextField testid="canvas-link-subtitle" label={t("editor.canvas.inspector.subtitleOptional")} value={el.subtitle ?? ""} onLive={(v) => onPatch(el.element_id, { subtitle: v })} onCommit={(v) => onPatchCommit(el.element_id, { subtitle: v })} />
            <TextField testid="canvas-link-url" label={t("editor.canvas.inspector.urlHttps")} value={el.url ?? ""} onCommit={(v) => onPatchCommit(el.element_id, { url: v })} />
            <a data-testid="canvas-link-preview" href={el.url && el.url.startsWith("https://") ? el.url : undefined} target="_blank" rel="noopener noreferrer" className="text-[11px] text-neon-teal">{t("editor.canvas.inspector.previewOpen")}</a>
            <LinkControls el={el} onPatchCommit={onPatchCommit} />
          </>)}
          {(el.type === "SOCIAL") && (
            <SocialControls el={el} onPatchCommit={onPatchCommit} />
          )}
          {(el.type === "CONTACTS") && (
            <ContactsControls el={el} onPatchCommit={onPatchCommit} />
          )}
          {el.type === "SHAPE" && el.shape_subtype !== "LINE" && <ColorSelect testid="canvas-fill-color" label={t("editor.canvas.inspector.fill")} value={el.fill_color ?? "GRID_STEEL"} onChange={(v) => onPatchCommit(el.element_id, { fill_color: v })} />}
          {el.type === "SHAPE" && el.shape_subtype === "LINE" && <ColorSelect testid="canvas-line-color" label={t("editor.canvas.inspector.lineColor")} value={el.line_color ?? "GRID_WHITE"} onChange={(v) => onPatchCommit(el.element_id, { line_color: v })} />}
          {(el.type === "IMAGE" || el.type === "GIF" || el.type === "VIDEO") && (
            <Select testid="canvas-fit" label={t("editor.canvas.inspector.fit")} value={String(el.present?.fit ?? "COVER")} options={["COVER", "CONTAIN"]} optLabel={(o) => t(`editor.canvas.enum.fit.${o}`)} onChange={(v) => onPatchCommit(el.element_id, { present: { ...(el.present ?? {}), fit: v } })} />
          )}
          {el.type === "PIXEL_ART" && el.pixel_art && (<>
            <div className="text-[9px] font-mono uppercase tracking-[0.2em] text-text-muted pt-1">{t("editor.canvas.inspector.pixelArt")} · {el.pixel_art.w}×{el.pixel_art.h}</div>
            <button data-testid="canvas-pixel-edit" onClick={() => onEditPixels(el.element_id)} className="chip w-full">◧ {t("editor.canvas.inspector.editPixels")}</button>
          </>)}
          {el.type === "PDF" && (<>
            <TextField testid="canvas-pdf-title" label={t("editor.canvas.inspector.title")} value={el.title ?? ""} onCommit={(v) => onPatchCommit(el.element_id, { title: v })} />
            <div className="text-[9px] font-mono uppercase tracking-[0.2em] text-text-muted pt-1">{t("editor.canvas.inspector.bgPreview")}</div>
            <div className="flex gap-1">
              <button data-testid="canvas-pdf-bg-pick" onClick={() => onPickPdfBg(el.element_id)} className="chip flex-1">{el.pdf_bg_asset_id ? t("editor.canvas.inspector.changeImage") : t("editor.canvas.inspector.addImage")}</button>
              {el.pdf_bg_asset_id && <button data-testid="canvas-pdf-bg-clear" onClick={() => onPatchCommit(el.element_id, { pdf_bg_asset_id: undefined })} className="chip-xs">✕</button>}
            </div>
          </>)}
        </div>
      )}
    </div>
  );
}

function FontControls({ el, onPatchCommit }: { el: CanvasElement; onPatchCommit: (id: string, p: Partial<CanvasElement>) => void }) {
  const t = useT();
  const ts = (el.text_style ?? {}) as Record<string, unknown>;
  const set = (p: Record<string, unknown>) => onPatchCommit(el.element_id, { text_style: { ...ts, ...p } });
  return (<>
    <Select testid="canvas-font" label={t("editor.canvas.controls.font")} value={(ts.font as string) ?? "GRID_SANS"} options={Object.keys(FONT_STACKS)} onChange={(v) => set({ font: v })} />
    <div className="flex gap-1">
      <button data-testid="canvas-bold" onClick={() => set({ bold: !ts.bold })} className={cn("chip", Boolean(ts.bold) && "!text-neon-teal")}>B</button>
      <button data-testid="canvas-italic" onClick={() => set({ italic: !ts.italic })} className={cn("chip", Boolean(ts.italic) && "!text-neon-teal")}>I</button>
      <button data-testid="canvas-underline" onClick={() => set({ underline: !ts.underline })} className={cn("chip", Boolean(ts.underline) && "!text-neon-teal")}>U</button>
    </div>
    <Select testid="canvas-align" label={t("editor.canvas.controls.align")} value={(ts.align as string) ?? "LEFT"} options={["LEFT", "CENTER", "RIGHT", "JUSTIFY"]} optLabel={(o) => t(`editor.canvas.enum.textAlign.${o}`)} onChange={(v) => set({ align: v })} />
    <ColorSelect testid="canvas-text-color" label={t("editor.canvas.controls.color")} value={(ts.color as string) ?? "GRID_WHITE"} onChange={(v) => set({ color: v })} />
  </>);
}

// ─────────────────────────── Social controls ───────────────────────────
function SocialControls({ el, onPatchCommit }: { el: CanvasElement; onPatchCommit: (id: string, p: Partial<CanvasElement>) => void }) {
  const t = useT();
  const ss = socialStyleWithDefaults(el.social_style);
  const dests = el.destinations ?? [];
  const setStyle = (p: Partial<SocialStyle>) => onPatchCommit(el.element_id, { social_style: { ...ss, ...p } });
  const applyPreset = (preset: SocialPreset) => onPatchCommit(el.element_id, { social_style: { ...ss, preset, ...SOCIAL_PRESET_DEFAULTS[preset] } });
  const setDest = (i: number, p: Record<string, string>) =>
    onPatchCommit(el.element_id, { destinations: dests.map((d, j) => (j === i ? { ...d, ...p } : d)) });
  const addDest = () => { if (dests.length >= 5) return; onPatchCommit(el.element_id, { destinations: [...dests, { platform: "x", url: "https://x.com/" }] }); };
  const removeDest = (i: number) => { if (dests.length <= 1) return; onPatchCommit(el.element_id, { destinations: dests.filter((_, j) => j !== i) }); };
  return (
    <div className="space-y-2">
      <div className="text-[9px] font-mono uppercase tracking-[0.2em] text-text-muted">{t("editor.canvas.controls.preset")}</div>
      <div className="grid grid-cols-2 gap-1">
        {(["ICONS_ONLY", "MINIMAL", "BUTTONS", "CARDS"] as SocialPreset[]).map((p) => (
          <button key={p} data-testid={`canvas-social-preset-${p}`} onClick={() => applyPreset(p)}
            className={cn("chip", ss.preset === p && "!text-neon-teal border-border-accent")}>{t(`editor.canvas.enum.socialPreset.${p}`)}</button>
        ))}
      </div>

      <div className="text-[9px] font-mono uppercase tracking-[0.2em] text-text-muted pt-1">{t("editor.canvas.social.links", { n: dests.length })}</div>
      {dests.map((d, i) => (
        <div key={i} data-testid={`canvas-social-dest-${i}`} className="rounded-sm border border-border-soft p-2 space-y-1.5">
          <div className="flex gap-1 items-center">
            <select data-testid={`canvas-social-platform-${i}`} value={d.platform} onChange={(e) => setDest(i, { platform: e.target.value })}
              className="flex-1 h-8 bg-obsidian border border-border-soft rounded-sm px-2 text-[12px] text-text-primary">
              {SOCIAL_PLATFORM_OPTIONS.map((p) => <option key={p} value={p}>{SOCIAL_PLATFORM_LABELS[p] ?? p}</option>)}
            </select>
            <button data-testid={`canvas-social-remove-${i}`} onClick={() => removeDest(i)} disabled={dests.length <= 1}
              title={t("editor.canvas.social.removeLink")} className="chip-xs disabled:opacity-30">✕</button>
          </div>
          <TextField testid={`canvas-social-url-${i}`} label={t("editor.canvas.inspector.urlHttps")} value={d.url ?? ""} onCommit={(v) => setDest(i, { url: v })} />
          <TextField testid={`canvas-social-label-${i}`} label={t("editor.canvas.controls.labelOptional")} value={d.label ?? ""} onCommit={(v) => setDest(i, { label: v })} />
        </div>
      ))}
      {dests.length < 5 && <button data-testid="canvas-social-add" onClick={addDest} className="chip w-full">{t("editor.canvas.social.addLink")}</button>}

      <div className="text-[9px] font-mono uppercase tracking-[0.2em] text-text-muted pt-1">{t("editor.canvas.controls.layout")}</div>
      <Select testid="canvas-social-direction" label={t("editor.canvas.controls.direction")} value={ss.direction} options={["HORIZONTAL", "VERTICAL", "GRID"]} optLabel={(o) => t(`editor.canvas.enum.direction.${o}`)} onChange={(v) => setStyle({ direction: v as SocialStyle["direction"] })} />
      {ss.direction === "GRID" && <NumRange testid="canvas-social-columns" label={t("editor.canvas.controls.columns", { v: ss.columns })} value={ss.columns} min={1} max={8} step={1} onChange={(v) => setStyle({ columns: v })} />}
      <Select testid="canvas-social-align" label={t("editor.canvas.controls.align")} value={ss.align} options={["START", "CENTER", "END"]} optLabel={(o) => t(`editor.canvas.enum.align.${o}`)} onChange={(v) => setStyle({ align: v as SocialStyle["align"] })} />
      <NumRange testid="canvas-social-gap" label={t("editor.canvas.controls.gap", { v: Math.round(ss.gap) })} value={ss.gap} min={0} max={80} step={1} onChange={(v) => setStyle({ gap: v })} />
      <NumRange testid="canvas-social-icon-size" label={t("editor.canvas.controls.iconSize", { v: Math.round(ss.icon_size) })} value={ss.icon_size} min={8} max={120} step={1} onChange={(v) => setStyle({ icon_size: v })} />
      <Toggle testid="canvas-social-show-label" label={t("editor.canvas.social.showLabels")} checked={ss.show_label} onChange={(v) => setStyle({ show_label: v })} />
      {ss.show_label && (<>
        <Select testid="canvas-social-font" label={t("editor.canvas.social.labelFont")} value={ss.font} options={Object.keys(FONT_STACKS)} onChange={(v) => setStyle({ font: v })} />
        <NumRange testid="canvas-social-font-size" label={t("editor.canvas.social.labelSize", { v: Math.round(ss.font_size) })} value={ss.font_size} min={6} max={64} step={1} onChange={(v) => setStyle({ font_size: v })} />
        <ColorSelect testid="canvas-social-label-color" label={t("editor.canvas.controls.labelColor")} value={ss.label_color} onChange={(v) => setStyle({ label_color: v })} />
      </>)}
      <Toggle testid="canvas-social-brand-colors" label={t("editor.canvas.controls.brandColors")} checked={ss.use_brand_colors} onChange={(v) => setStyle({ use_brand_colors: v })} />
      {!ss.use_brand_colors && <ColorSelect testid="canvas-social-icon-color" label={t("editor.canvas.controls.iconColor")} value={ss.icon_color} onChange={(v) => setStyle({ icon_color: v })} />}
      <Toggle testid="canvas-social-bg" label={t("editor.canvas.controls.backgroundChip")} checked={ss.bg_enabled} onChange={(v) => setStyle({ bg_enabled: v })} />
      {ss.bg_enabled && <ColorSelect testid="canvas-social-bg-color" label={t("editor.canvas.controls.bgColor")} value={ss.bg_color} onChange={(v) => setStyle({ bg_color: v })} />}
      <Toggle testid="canvas-social-border" label={t("editor.canvas.controls.border")} checked={ss.border_enabled} onChange={(v) => setStyle({ border_enabled: v })} />
      {ss.border_enabled && <ColorSelect testid="canvas-social-border-color" label={t("editor.canvas.controls.borderColor")} value={ss.border_color} onChange={(v) => setStyle({ border_color: v })} />}
      <Select testid="canvas-social-radius" label={t("editor.canvas.controls.radius")} value={ss.radius} options={["SQUARE", "SLIGHT", "ROUNDED", "PILL"]} optLabel={(o) => t(`editor.canvas.enum.radius.${o}`)} onChange={(v) => setStyle({ radius: v as SocialStyle["radius"] })} />
      <NumRange testid="canvas-social-padding" label={t("editor.canvas.controls.padding", { v: Math.round(ss.padding) })} value={ss.padding} min={0} max={60} step={1} onChange={(v) => setStyle({ padding: v })} />
      <Select testid="canvas-social-hover" label={t("editor.canvas.controls.hover")} value={ss.hover} options={["NONE", "LIFT", "GLOW", "SCALE"]} optLabel={(o) => t(`editor.canvas.enum.hover.${o}`)} onChange={(v) => setStyle({ hover: v as SocialStyle["hover"] })} />
    </div>
  );
}

// ─────────────────────────── Contacts controls ───────────────────────────
function ContactsControls({ el, onPatchCommit }: { el: CanvasElement; onPatchCommit: (id: string, p: Partial<CanvasElement>) => void }) {
  const t = useT();
  const cs = contactsStyleWithDefaults(el.contacts_style);
  const entries = el.entries ?? [];
  const setStyle = (p: Partial<ContactsStyle>) => onPatchCommit(el.element_id, { contacts_style: { ...cs, ...p } });
  const applyPreset = (preset: ContactsPreset) => onPatchCommit(el.element_id, { contacts_style: { ...cs, preset, ...CONTACTS_PRESET_DEFAULTS[preset] } });
  const setEntry = (i: number, p: Record<string, string>) =>
    onPatchCommit(el.element_id, { entries: entries.map((e, j) => (j === i ? { ...e, ...p } : e)) });
  const addEntry = () => { if (entries.length >= 10) return; onPatchCommit(el.element_id, { entries: [...entries, { kind: "EMAIL", value: "hello@example.com" }] }); };
  const removeEntry = (i: number) => { if (entries.length <= 1) return; onPatchCommit(el.element_id, { entries: entries.filter((_, j) => j !== i) }); };
  const placeholder: Record<string, string> = { EMAIL: t("editor.canvas.contacts.placeholder.EMAIL"), PHONE: t("editor.canvas.contacts.placeholder.PHONE"), WHATSAPP: t("editor.canvas.contacts.placeholder.WHATSAPP"), WEBSITE: t("editor.canvas.contacts.placeholder.WEBSITE"), MAP: t("editor.canvas.contacts.placeholder.MAP") };
  return (
    <div className="space-y-2">
      <div className="text-[9px] font-mono uppercase tracking-[0.2em] text-text-muted">{t("editor.canvas.controls.preset")}</div>
      <div className="grid grid-cols-3 gap-1">
        {(["ICONS_ONLY", "BUSINESS", "MINIMAL"] as ContactsPreset[]).map((p) => (
          <button key={p} data-testid={`canvas-contacts-preset-${p}`} onClick={() => applyPreset(p)}
            className={cn("chip !px-1", cs.preset === p && "!text-neon-teal border-border-accent")}>{t(`editor.canvas.enum.contactsPreset.${p}`)}</button>
        ))}
      </div>

      <div className="text-[9px] font-mono uppercase tracking-[0.2em] text-text-muted pt-1">{t("editor.canvas.contacts.heading", { n: entries.length })}</div>
      {entries.map((e, i) => (
        <div key={i} data-testid={`canvas-contacts-entry-${i}`} className="rounded-sm border border-border-soft p-2 space-y-1.5">
          <div className="flex gap-1 items-center">
            <select data-testid={`canvas-contacts-kind-${i}`} value={e.kind} onChange={(ev) => setEntry(i, { kind: ev.target.value })}
              className="flex-1 h-8 bg-obsidian border border-border-soft rounded-sm px-2 text-[12px] text-text-primary">
              {CONTACT_KIND_OPTIONS.map((k) => <option key={k} value={k}>{t(`editor.canvas.contacts.kind.${k}`)}</option>)}
            </select>
            <button data-testid={`canvas-contacts-remove-${i}`} onClick={() => removeEntry(i)} disabled={entries.length <= 1}
              title={t("editor.canvas.contacts.removeContact")} className="chip-xs disabled:opacity-30">✕</button>
          </div>
          <TextField testid={`canvas-contacts-value-${i}`} label={placeholder[e.kind] ?? t("editor.canvas.contacts.value")} value={e.value ?? ""} onCommit={(v) => setEntry(i, { value: v })} />
          <TextField testid={`canvas-contacts-label-${i}`} label={t("editor.canvas.controls.labelOptional")} value={e.label ?? ""} onCommit={(v) => setEntry(i, { label: v })} />
        </div>
      ))}
      {entries.length < 10 && <button data-testid="canvas-contacts-add" onClick={addEntry} className="chip w-full">{t("editor.canvas.contacts.addContact")}</button>}

      <div className="text-[9px] font-mono uppercase tracking-[0.2em] text-text-muted pt-1">{t("editor.canvas.controls.layout")}</div>
      <Select testid="canvas-contacts-display" label={t("editor.canvas.contacts.show")} value={cs.display} options={["ICON", "ICON_LABEL", "ICON_DATA", "BUTTON"]} optLabel={(o) => t(`editor.canvas.enum.contactsDisplay.${o}`)} onChange={(v) => setStyle({ display: v as ContactsStyle["display"] })} />
      <Select testid="canvas-contacts-direction" label={t("editor.canvas.controls.direction")} value={cs.direction} options={["HORIZONTAL", "VERTICAL", "GRID"]} optLabel={(o) => t(`editor.canvas.enum.direction.${o}`)} onChange={(v) => setStyle({ direction: v as ContactsStyle["direction"] })} />
      {cs.direction === "GRID" && <NumRange testid="canvas-contacts-columns" label={t("editor.canvas.controls.columns", { v: cs.columns })} value={cs.columns} min={1} max={8} step={1} onChange={(v) => setStyle({ columns: v })} />}
      <Select testid="canvas-contacts-align" label={t("editor.canvas.controls.align")} value={cs.align} options={["START", "CENTER", "END"]} optLabel={(o) => t(`editor.canvas.enum.align.${o}`)} onChange={(v) => setStyle({ align: v as ContactsStyle["align"] })} />
      <NumRange testid="canvas-contacts-gap" label={t("editor.canvas.controls.gap", { v: Math.round(cs.gap) })} value={cs.gap} min={0} max={80} step={1} onChange={(v) => setStyle({ gap: v })} />
      <NumRange testid="canvas-contacts-icon-size" label={t("editor.canvas.controls.iconSize", { v: Math.round(cs.icon_size) })} value={cs.icon_size} min={8} max={120} step={1} onChange={(v) => setStyle({ icon_size: v })} />
      <Select testid="canvas-contacts-font" label={t("editor.canvas.controls.font")} value={cs.font} options={Object.keys(FONT_STACKS)} onChange={(v) => setStyle({ font: v })} />
      <NumRange testid="canvas-contacts-font-size" label={t("editor.canvas.controls.textSize", { v: Math.round(cs.font_size) })} value={cs.font_size} min={6} max={64} step={1} onChange={(v) => setStyle({ font_size: v })} />
      <Toggle testid="canvas-contacts-brand-colors" label={t("editor.canvas.controls.brandColors")} checked={cs.use_brand_colors} onChange={(v) => setStyle({ use_brand_colors: v })} />
      {!cs.use_brand_colors && <ColorSelect testid="canvas-contacts-icon-color" label={t("editor.canvas.controls.iconColor")} value={cs.icon_color} onChange={(v) => setStyle({ icon_color: v })} />}
      <ColorSelect testid="canvas-contacts-label-color" label={t("editor.canvas.controls.labelColor")} value={cs.label_color} onChange={(v) => setStyle({ label_color: v })} />
      <ColorSelect testid="canvas-contacts-data-color" label={t("editor.canvas.contacts.dataColor")} value={cs.data_color} onChange={(v) => setStyle({ data_color: v })} />
      <Toggle testid="canvas-contacts-bg" label={t("editor.canvas.controls.backgroundChip")} checked={cs.bg_enabled} onChange={(v) => setStyle({ bg_enabled: v })} />
      {cs.bg_enabled && <ColorSelect testid="canvas-contacts-bg-color" label={t("editor.canvas.controls.bgColor")} value={cs.bg_color} onChange={(v) => setStyle({ bg_color: v })} />}
      <Toggle testid="canvas-contacts-border" label={t("editor.canvas.controls.border")} checked={cs.border_enabled} onChange={(v) => setStyle({ border_enabled: v })} />
      {cs.border_enabled && <ColorSelect testid="canvas-contacts-border-color" label={t("editor.canvas.controls.borderColor")} value={cs.border_color} onChange={(v) => setStyle({ border_color: v })} />}
      <Select testid="canvas-contacts-radius" label={t("editor.canvas.controls.radius")} value={cs.radius} options={["SQUARE", "SLIGHT", "ROUNDED", "PILL"]} optLabel={(o) => t(`editor.canvas.enum.radius.${o}`)} onChange={(v) => setStyle({ radius: v as ContactsStyle["radius"] })} />
      <NumRange testid="canvas-contacts-padding" label={t("editor.canvas.controls.padding", { v: Math.round(cs.padding) })} value={cs.padding} min={0} max={60} step={1} onChange={(v) => setStyle({ padding: v })} />
      <Select testid="canvas-contacts-hover" label={t("editor.canvas.controls.hover")} value={cs.hover} options={["NONE", "LIFT", "GLOW", "SCALE"]} optLabel={(o) => t(`editor.canvas.enum.hover.${o}`)} onChange={(v) => setStyle({ hover: v as ContactsStyle["hover"] })} />
    </div>
  );
}

// ─────────────────────────── Link controls ───────────────────────────
function LinkControls({ el, onPatchCommit }: { el: CanvasElement; onPatchCommit: (id: string, p: Partial<CanvasElement>) => void }) {
  const t = useT();
  const ls = linkStyleWithDefaults(el.link_style);
  const setStyle = (p: Partial<LinkStyle>) => onPatchCommit(el.element_id, { link_style: { ...ls, ...p } });
  const applyPreset = (preset: LinkPreset) => onPatchCommit(el.element_id, { link_style: { ...ls, preset, ...LINK_PRESET_DEFAULTS[preset] } });
  return (
    <div className="space-y-2 pt-1">
      <div className="text-[9px] font-mono uppercase tracking-[0.2em] text-text-muted">{t("editor.canvas.controls.preset")}</div>
      <div className="grid grid-cols-2 gap-1">
        {(["TEXT", "BUTTON", "OUTLINE", "CARD"] as LinkPreset[]).map((p) => (
          <button key={p} data-testid={`canvas-link-preset-${p}`} onClick={() => applyPreset(p)}
            className={cn("chip", ls.preset === p && "!text-neon-teal border-border-accent")}>{t(`editor.canvas.enum.linkPreset.${p}`)}</button>
        ))}
      </div>
      <div className="text-[9px] font-mono uppercase tracking-[0.2em] text-text-muted pt-1">{t("editor.canvas.controls.style")}</div>
      <Select testid="canvas-link-icon" label={t("editor.canvas.controls.icon")} value={ls.icon} options={["NONE", "ARROW", "FAVICON"]} optLabel={(o) => t(`editor.canvas.enum.linkIcon.${o}`)} onChange={(v) => setStyle({ icon: v as LinkStyle["icon"] })} />
      <Select testid="canvas-link-align" label={t("editor.canvas.controls.align")} value={ls.align} options={["START", "CENTER", "END"]} optLabel={(o) => t(`editor.canvas.enum.align.${o}`)} onChange={(v) => setStyle({ align: v as LinkStyle["align"] })} />
      <Select testid="canvas-link-font" label={t("editor.canvas.controls.font")} value={ls.font} options={Object.keys(FONT_STACKS)} onChange={(v) => setStyle({ font: v })} />
      <NumRange testid="canvas-link-font-size" label={t("editor.canvas.controls.textSize", { v: Math.round(ls.font_size) })} value={ls.font_size} min={6} max={72} step={1} onChange={(v) => setStyle({ font_size: v })} />
      <ColorSelect testid="canvas-link-label-color" label={t("editor.canvas.controls.labelColor")} value={ls.label_color} onChange={(v) => setStyle({ label_color: v })} />
      <ColorSelect testid="canvas-link-subtitle-color" label={t("editor.canvas.controls.subtitleColor")} value={ls.subtitle_color} onChange={(v) => setStyle({ subtitle_color: v })} />
      <Toggle testid="canvas-link-underline" label={t("editor.canvas.controls.underline")} checked={ls.underline} onChange={(v) => setStyle({ underline: v })} />
      <Toggle testid="canvas-link-bg" label={t("editor.canvas.inspector.background")} checked={ls.bg_enabled} onChange={(v) => setStyle({ bg_enabled: v })} />
      {ls.bg_enabled && <ColorSelect testid="canvas-link-bg-color" label={t("editor.canvas.controls.bgColor")} value={ls.bg_color} onChange={(v) => setStyle({ bg_color: v })} />}
      <Toggle testid="canvas-link-border" label={t("editor.canvas.controls.border")} checked={ls.border_enabled} onChange={(v) => setStyle({ border_enabled: v })} />
      {ls.border_enabled && <ColorSelect testid="canvas-link-border-color" label={t("editor.canvas.controls.borderColor")} value={ls.border_color} onChange={(v) => setStyle({ border_color: v })} />}
      <Select testid="canvas-link-radius" label={t("editor.canvas.controls.radius")} value={ls.radius} options={["SQUARE", "SLIGHT", "ROUNDED", "PILL"]} optLabel={(o) => t(`editor.canvas.enum.radius.${o}`)} onChange={(v) => setStyle({ radius: v as LinkStyle["radius"] })} />
      <NumRange testid="canvas-link-padding" label={t("editor.canvas.controls.padding", { v: Math.round(ls.padding) })} value={ls.padding} min={0} max={60} step={1} onChange={(v) => setStyle({ padding: v })} />
      <Select testid="canvas-link-hover" label={t("editor.canvas.controls.hover")} value={ls.hover} options={["NONE", "LIFT", "GLOW", "SCALE"]} optLabel={(o) => t(`editor.canvas.enum.hover.${o}`)} onChange={(v) => setStyle({ hover: v as LinkStyle["hover"] })} />
    </div>
  );
}

// ─────────────────────────── Layers ───────────────────────────
function Layers({ elements, selected, setSelected, onReorder, onToggle }: {
  elements: CanvasElement[]; selected: string[]; setSelected: (ids: string[]) => void;
  onReorder: (next: CanvasElement[]) => void; onToggle: (id: string, k: "hidden" | "locked") => void;
}) {
  const t = useT();
  const ordered = [...elements].sort((a, b) => b.z - a.z); // top = front
  return (
    <div data-testid="canvas-layers" className="p-3">
      <div className="text-[9px] font-mono uppercase tracking-[0.2em] text-text-muted mb-2">{t("editor.canvas.layers.title")}</div>
      {ordered.length === 0 && <div className="text-[11px] text-text-muted font-mono">{t("editor.canvas.layers.empty")}</div>}
      <div className="space-y-1">
        {ordered.map((el) => (
          <div key={el.element_id} data-testid={`canvas-layer-${el.element_id}`} data-selected={selected.includes(el.element_id)}
            onClick={() => setSelected([el.element_id])}
            className={cn("flex items-center gap-1 px-2 h-8 rounded-sm border cursor-pointer text-[11px] font-mono",
              selected.includes(el.element_id) ? "border-border-accent bg-white/5" : "border-border-soft")}>
            <span className="flex-1 truncate">{t(`editor.canvas.enum.module.${el.type}`)}{el.shape_subtype ? `·${el.shape_subtype[0]}` : ""}</span>
            <button data-testid={`canvas-front-${el.element_id}`} onClick={(e) => { e.stopPropagation(); onReorder(bringToFront(elements, el.element_id)); }} className="chip-xs" title={t("editor.canvas.layers.front")}>⤒</button>
            <button data-testid={`canvas-forward-${el.element_id}`} onClick={(e) => { e.stopPropagation(); onReorder(bringForward(elements, el.element_id)); }} className="chip-xs" title={t("editor.canvas.layers.forward")}>↑</button>
            <button data-testid={`canvas-backward-${el.element_id}`} onClick={(e) => { e.stopPropagation(); onReorder(sendBackward(elements, el.element_id)); }} className="chip-xs" title={t("editor.canvas.layers.backward")}>↓</button>
            <button data-testid={`canvas-back-${el.element_id}`} onClick={(e) => { e.stopPropagation(); onReorder(sendToBack(elements, el.element_id)); }} className="chip-xs" title={t("editor.canvas.layers.back")}>⤓</button>
            <button data-testid={`canvas-hide-${el.element_id}`} onClick={(e) => { e.stopPropagation(); onToggle(el.element_id, "hidden"); }} className={cn("chip-xs", el.hidden && "!text-state-danger")} title={t("editor.canvas.layers.hide")}>{el.hidden ? "◌" : "◉"}</button>
            <button data-testid={`canvas-lock-${el.element_id}`} onClick={(e) => { e.stopPropagation(); onToggle(el.element_id, "locked"); }} className={cn("chip-xs", el.locked && "!text-neon-teal")} title={t("editor.canvas.layers.lock")}>{el.locked ? "🔒" : "🔓"}</button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─────────────────── In-editor Media Library (slide-down) ───────────────────
// Upload + browse the wallet media library WITHOUT leaving the canvas. Reuses
// the exact dashboard hook (wallet-scoped, CSRF, quota) so a freshly uploaded
// asset is immediately available to the Add-media pickers on the same screen.
function CanvasMediaLibraryPanel({ onClose }: { onClose: () => void }) {
  const t = useT();
  const { state, loadMore, upload, remove } = useMediaLibrary();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const onPick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setNotice(null);
    const res = await upload(file);
    if (!res.ok && res.error) setNotice(res.error);
  };

  const usage = state.usage;
  const pct = usage ? Math.min(100, Math.round((usage.used_bytes / usage.limit_bytes) * 100)) : 0;

  return (
    <div
      data-testid="canvas-media-panel"
      className="shrink-0 border-b border-border-soft bg-panel overflow-hidden animate-[canvasMediaSlide_180ms_ease-out]"
    >
      <div className="max-h-[42vh] overflow-y-auto p-3">
        <div className="flex items-center gap-3 flex-wrap">
          <span className="font-mono text-[11px] uppercase tracking-[0.16em] text-text-primary">{t("editor.canvas.mediaPanel.title")}</span>
          <input ref={inputRef} data-testid="canvas-media-upload-input" type="file" accept={CANVAS_MEDIA_ACCEPT} className="hidden" onChange={onPick} />
          <button
            type="button"
            data-testid="canvas-media-upload-button"
            disabled={state.busy}
            onClick={() => inputRef.current?.click()}
            className="inline-flex items-center gap-2 h-8 px-4 rounded-md border border-border-accent bg-transparent text-neon-teal font-mono text-[11px] uppercase tracking-[0.16em] hover:bg-neon-teal-soft disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              <path d="M16 16l-4-4-4 4" /><path d="M12 12v9" /><path d="M20.4 14.5A5 5 0 0016 6.5 6.5 6.5 0 004 9a4.5 4.5 0 00.5 9h3" />
            </svg>
            {state.busy ? t("editor.canvas.mediaPanel.working") : t("editor.canvas.mediaPanel.upload")}
          </button>
          {usage && (
            <div className="flex items-center gap-2">
              <div className="h-1.5 w-24 rounded-full bg-border-soft overflow-hidden"><div className="h-full bg-state-success transition-[width] duration-300" style={{ width: `${pct}%` }} /></div>
              <span data-testid="canvas-media-usage" className="font-mono text-[10px] text-text-muted whitespace-nowrap">{usage ? `${fmtSize(usage.used_bytes)} / ${fmtSize(usage.limit_bytes)}` : ""}</span>
            </div>
          )}
          <button type="button" data-testid="canvas-media-panel-close" onClick={onClose} className="chip ml-auto">{t("editor.canvas.close")}</button>
        </div>

        <p className="mt-2 text-[10px] font-mono text-text-muted">
          {t("editor.canvas.mediaPanel.hint")}
        </p>

        {notice && <p data-testid="canvas-media-panel-notice" className="mt-2 text-[11px] text-state-danger font-mono">{notice}</p>}

        <div className="mt-3">
          {state.status === "loading" && <p data-testid="canvas-media-panel-loading" className="text-[11px] font-mono text-text-muted">{t("editor.canvas.mediaPanel.loading")}</p>}
          {state.status === "error" && <p data-testid="canvas-media-panel-error" className="text-[11px] font-mono text-state-danger">{state.error}</p>}
          {state.status === "ready" && state.assets.length === 0 && (
            <p data-testid="canvas-media-panel-empty" className="rounded-md border border-dashed border-border-soft p-4 text-[11px] font-mono text-text-muted">
              {t("editor.canvas.mediaPanel.empty")}
            </p>
          )}
          {state.status === "ready" && state.assets.length > 0 && (
            <div data-testid="canvas-media-panel-grid" className="grid grid-cols-4 sm:grid-cols-6 lg:grid-cols-8 gap-2">
              {state.assets.map((a) => (
                <div key={a.asset_id} data-testid={`canvas-media-panel-card-${a.asset_id}`} className="group relative overflow-hidden rounded-md border border-border-soft bg-obsidian">
                  <div className="flex aspect-square items-center justify-center overflow-hidden bg-black/40">
                    {a.media_type === "image" || a.media_type === "gif" ? (
                      <img src={mediaContentUrl(a)} alt={a.filename ?? "media"} loading="lazy" className="h-full w-full object-cover" />
                    ) : (
                      <span className="font-mono text-[9px] uppercase tracking-[0.1em] text-text-muted">{a.media_type}</span>
                    )}
                  </div>
                  <button
                    type="button"
                    data-testid={`canvas-media-panel-delete-${a.asset_id}`}
                    disabled={state.busy}
                    onClick={() => void remove(a.asset_id)}
                    aria-label={t("editor.canvas.mediaPanel.deleteAria")}
                    className="absolute top-1 right-1 h-6 w-6 inline-flex items-center justify-center rounded-md bg-black/60 text-text-muted opacity-0 group-hover:opacity-100 hover:text-state-danger disabled:opacity-40"
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18" /><path d="M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2" /><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" /></svg>
                  </button>
                </div>
              ))}
            </div>
          )}
          {state.status === "ready" && state.nextCursor && (
            <div className="mt-3 flex justify-center">
              <button type="button" data-testid="canvas-media-panel-load-more" onClick={() => void loadMore()} className="chip">{t("editor.canvas.mediaPanel.loadMore")}</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


// ─────────────────────────── Media picker ───────────────────────────
function MediaPicker({ moduleType, onPick, onClose }: { moduleType: ModuleType; onPick: (a: MediaAsset) => void; onClose: () => void }) {
  const t = useT();
  const [assets, setAssets] = useState<MediaAsset[]>([]);
  const want = moduleType === "IMAGE" ? "image" : moduleType === "GIF" ? "gif" : moduleType === "VIDEO" ? "video" : "pdf";
  const wantLabel = t(`editor.canvas.mediaKind.${want}`);
  useEffect(() => {
    const c = new AbortController();
    listAssets(null, 60, c.signal).then((r) => setAssets(r.assets.filter((a) => a.media_type === want))).catch(() => {});
    return () => c.abort();
  }, [want]);
  return (
    <div data-testid="canvas-media-picker" className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-6" onClick={onClose}>
      <div className="w-[min(720px,95%)] max-h-[80vh] overflow-y-auto rounded-lg border border-border-soft bg-panel p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <div className="font-mono text-[12px] uppercase tracking-[0.16em]">{t("editor.canvas.mediaPicker.choose", { kind: wantLabel })}</div>
          <button data-testid="canvas-media-close" onClick={onClose} className="chip">{t("editor.canvas.close")}</button>
        </div>
        {assets.length === 0 && <div data-testid="canvas-media-empty" className="text-[11px] text-text-muted font-mono">{t("editor.canvas.mediaPicker.empty", { kind: wantLabel })}</div>}
        <div className="grid grid-cols-3 gap-3">
          {assets.map((a) => (
            <button key={a.asset_id} data-testid={`canvas-media-asset-${a.asset_id}`} onClick={() => onPick(a)}
              className="border border-border-soft rounded-sm p-2 hover:border-border-accent text-left">
              {want === "image" || want === "gif" ? <img src={mediaContentUrl(a)} alt="" className="w-full h-20 object-cover rounded-sm" /> : <div className="w-full h-20 flex items-center justify-center bg-obsidian text-text-muted text-[10px] font-mono">{want.toUpperCase()}</div>}
              <div className="mt-1 text-[10px] font-mono text-text-secondary truncate">{a.filename ?? a.asset_id.slice(0, 8)}</div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────── GP meter ───────────────────────────
function GpMeterBar({ usedGp, capacityGp, availableGp, over }: { usedGp: number; capacityGp: number; availableGp: number; over: boolean }) {
  const t = useT();
  const pct = capacityGp > 0 ? Math.min(100, (usedGp / capacityGp) * 100) : (usedGp > 0 ? 100 : 0);
  return (
    <div data-testid="canvas-gp-meter" data-over={over} className="flex items-center gap-2">
      <div className="flex flex-col leading-none">
        <span className="text-[10px] font-mono text-text-primary whitespace-nowrap">
          {t("editor.canvas.gp.used")} <span data-testid="gp-used">{usedGp}</span> / <span data-testid="gp-capacity">{capacityGp}</span> GP
        </span>
        <span data-testid="gp-available" className={cn("text-[9px] font-mono", over ? "text-state-danger" : "text-text-secondary")}>
          {t("editor.canvas.gp.available", { n: availableGp })}
        </span>
      </div>
      <div className="w-24 h-1.5 rounded-full bg-border-soft overflow-hidden" aria-hidden>
        <div className="h-full transition-[width] duration-150"
          style={{ width: `${pct}%`, background: over ? "#ff5470" : "#22d3ee" }} />
      </div>
      {over && <span data-testid="gp-over-capacity" className="text-[9px] font-mono uppercase tracking-[0.12em] text-state-danger">{t("editor.canvas.gp.overCapacity")}</span>}
    </div>
  );
}

// ─────────────────────────── Save error modal ───────────────────────────
function SaveErrorModal({ code, detail, onClose, onReload }: { code: string; detail: string; onClose: () => void; onReload?: () => void }) {
  const t = useT();
  return (
    <div data-testid="canvas-error-modal" className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 p-6" onClick={onClose}>
      <div className="w-[min(520px,95%)] rounded-lg border border-state-danger/50 bg-panel p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <div className="font-mono text-[12px] uppercase tracking-[0.16em] text-state-danger">{t("editor.canvas.errorModal.saveFailed")}</div>
          <button data-testid="canvas-error-close" onClick={onClose} className="chip">{t("editor.canvas.close")}</button>
        </div>
        <div className="text-[10px] font-mono uppercase tracking-[0.16em] text-text-muted mb-1">{code}</div>
        <div data-testid="canvas-error-detail" className="text-[13px] text-text-primary font-mono whitespace-pre-wrap break-words leading-relaxed">{detail}</div>
        <div className="mt-4 flex items-center gap-2 justify-end">
          {onReload && <button data-testid="canvas-error-reload" onClick={onReload} className="chip">{t("editor.error.reload")}</button>}
          <button data-testid="canvas-error-dismiss" onClick={onClose} className="inline-flex items-center h-8 px-4 rounded-md bg-text-primary text-text-inverse font-mono text-[11px] uppercase tracking-[0.16em] hover:opacity-90">{t("editor.canvas.errorModal.gotIt")}</button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────── small inputs ───────────────────────────
function Select({ label, value, options, onChange, testid, optLabel }: { label: string; value: string; options: string[]; onChange: (v: string) => void; testid: string; optLabel?: (o: string) => string }) {
  return (<label className="block"><span className="block text-[10px] font-mono text-text-muted mb-0.5">{label}</span>
    <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} className="w-full h-8 bg-obsidian border border-border-soft rounded-sm px-2 text-[12px] text-text-primary">
      {options.map((o) => <option key={o} value={o}>{optLabel ? optLabel(o) : o}</option>)}</select></label>);
}
function ColorSelect({ label, value, onChange, testid }: { label: string; value: string; onChange: (v: string) => void; testid: string }) {
  return (<label className="block"><span className="block text-[10px] font-mono text-text-muted mb-0.5">{label}</span>
    <div className="flex items-center gap-2"><span className="h-5 w-5 rounded-sm border border-border-soft" style={{ background: hexOf(value) }} />
      <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} className="flex-1 h-8 bg-obsidian border border-border-soft rounded-sm px-2 text-[12px]">
        {Object.keys(COLOR_HEX).map((o) => <option key={o} value={o}>{o.replace("GRID_", "")}</option>)}</select></div></label>);
}
function Range({ label, value, onChange, testid }: { label: string; value: number; onChange: (v: number) => void; testid: string }) {
  return (<label className="block"><span className="block text-[10px] font-mono text-text-muted mb-0.5">{label}</span>
    <input data-testid={testid} type="range" min={0} max={1} step={0.01} value={value} onChange={(e) => onChange(parseFloat(e.target.value))} className="w-full" /></label>);
}
function NumRange({ label, value, min, max, step, onChange, testid }: { label: string; value: number; min: number; max: number; step: number; onChange: (v: number) => void; testid: string }) {
  return (<label className="block"><span className="block text-[10px] font-mono text-text-muted mb-0.5">{label}</span>
    <input data-testid={testid} type="range" min={min} max={max} step={step} value={value} onChange={(e) => onChange(parseFloat(e.target.value))} className="w-full" /></label>);
}
function Toggle({ label, checked, onChange, testid }: { label: string; checked: boolean; onChange: (v: boolean) => void; testid: string }) {
  return (<button data-testid={testid} data-checked={checked} onClick={() => onChange(!checked)}
    className={cn("chip w-full !justify-between", checked && "!text-neon-teal border-border-accent")}>
    <span>{label}</span><span className="text-[9px]">{checked ? "ON" : "OFF"}</span></button>);
}
function TextField({ label, value, onCommit, onLive, testid }: { label: string; value: string; onCommit: (v: string) => void; onLive?: (v: string) => void; testid: string }) {
  const [v, setV] = useState(value);
  useEffect(() => { setV(value); }, [value]);
  return (<label className="block"><span className="block text-[10px] font-mono text-text-muted mb-0.5">{label}</span>
    <input data-testid={testid} value={v}
      onChange={(e) => { setV(e.target.value); onLive?.(e.target.value); }}
      onBlur={(e) => onCommit(e.target.value)}
      className="w-full h-8 bg-obsidian border border-border-soft rounded-sm px-2 text-[12px]" /></label>);
}
